import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, ChevronDown, Shapes, Palette, Triangle, Calculator, FolderOpen, Youtube, FileText, Minus, Plus } from 'lucide-react';
import {
  appendDisplayDrawingStrokes,
  replaceDisplayDrawingStrokes,
  dismissDisplayItem,
  fetchDisplayStatus,
  registerDisplay,
  releaseDisplayConnection,
  updateDisplayLayout,
  deviceUpdateDisplayTextItem,
  updateDisplayOverlays,
  deviceSetActivePage,
  deviceAddPage,
  type DisplayStatus
} from '../lib/displayApi';
import { api } from '../lib/api';
import logo from '../assets/logo-negative.png';
import idleLogo from '../assets/logo-idle-transparent.png';
import spruecheRaw from '../assets/sprueche.txt?raw';
import { GeodreieckOverlay, DEFAULT_GEODREIECK, type GeodreieckState } from '../components/GeodreieckOverlay';
import { CalculatorOverlay, DEFAULT_CALCULATOR, type CalculatorState } from '../components/CalculatorOverlay';
import { ZirkelOverlay, DEFAULT_ZIRKEL, arcPoints, LEG_LENGTH_MIN, LEG_LENGTH_MAX, type ZirkelState } from '../components/ZirkelOverlay';
import LiveLessonDisplayOverlayStack from '../components/liveLesson/LiveLessonDisplayOverlayStack';
import ClassroomBoardView from '../components/liveLesson/ClassroomBoardView';

const DISPLAY_DEVICE_TOKEN_KEY = 'displayDeviceToken';

function detectCapabilities() {
  const hasWindow = typeof window !== 'undefined';
  const hasNavigator = typeof navigator !== 'undefined';
  const cssSupports = hasWindow && typeof window.CSS !== 'undefined' && typeof window.CSS.supports === 'function';
  const hasStorage =
    hasWindow &&
    (() => {
      try {
        return Boolean(window.localStorage);
      } catch (error) {
        return false;
      }
    })();

  return {
    features: {
      localStorage: hasStorage,
      websocket: hasWindow && typeof window.WebSocket !== 'undefined',
      fetch: hasWindow && typeof window.fetch === 'function',
      cssGrid: cssSupports ? window.CSS.supports('display', 'grid') : false,
      cssVariables: cssSupports ? window.CSS.supports('--display-color', '#000') : false,
      esModules: hasWindow && 'noModule' in document.createElement('script')
    },
    screen: hasWindow
      ? {
          width: window.screen?.width || null,
          height: window.screen?.height || null,
          pixelRatio: window.devicePixelRatio || 1
        }
      : null,
    viewport: hasWindow
      ? {
          width: window.innerWidth || null,
          height: window.innerHeight || null,
          pixelRatio: window.devicePixelRatio || 1
        }
      : null,
    platform: hasNavigator ? navigator.platform || 'unknown' : 'unknown',
    userAgent: hasNavigator ? navigator.userAgent || '' : ''
  };
}

function extractDisplayTheme(rawTheme: string | undefined | null): string {
  const value = String(rawTheme || '');
  if (!value) {
    return '';
  }
  const parts = value.split(/\s+[–-]\s+/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return parts[parts.length - 1];
  }
  return value;
}

function buildPdfDisplayUrl(rawUrl: string): string {
  const baseUrl = String(rawUrl || '').trim();
  if (!baseUrl) {
    return '';
  }
  const toolbarFlags = 'toolbar=0&navpanes=0&scrollbar=0';
  if (baseUrl.includes('#')) {
    return baseUrl.includes('toolbar=') ? baseUrl : `${baseUrl}&${toolbarFlags}`;
  }
  return `${baseUrl}#${toolbarFlags}`;
}

function buildYouTubeEmbedUrl(rawUrl: string): string {
  const value = String(rawUrl || '').trim();
  if (!value) {
    return '';
  }
  try {
    const parsed = new URL(value);
    const host = parsed.hostname.toLowerCase();
    let id = '';
    if (host.includes('youtu.be')) {
      id = parsed.pathname.replace('/', '').trim();
    } else if (host.includes('youtube.com')) {
      if (parsed.pathname.startsWith('/embed/')) {
        id = parsed.pathname.replace('/embed/', '').split('/')[0];
      } else if (parsed.pathname.startsWith('/shorts/')) {
        id = parsed.pathname.replace('/shorts/', '').split('/')[0];
      } else if (parsed.pathname.startsWith('/live/')) {
        id = parsed.pathname.replace('/live/', '').split('/')[0];
      } else {
        id = parsed.searchParams.get('v') || '';
      }
    }
    if (!id) {
      return value;
    }
    return `https://www.youtube-nocookie.com/embed/${id}?rel=0`;
  } catch (error) {
    return value;
  }
}

const DRAW_COLORS = ['#FFFFFF', '#EF4444', '#FACC15', '#3B82F6', '#22C55E', '#000000'];
const DRAW_TOOLS = ['pen', 'marker', 'line', 'line-dashed', 'line-ruler', 'eraser'] as const;
type DrawTool = (typeof DRAW_TOOLS)[number];
/** Linien mit zwei Punkten (wie Line, gestrichelt, Skalenlinie). */
const LINE_TWO_POINT_TOOLS = ['line', 'line-dashed', 'line-ruler'] as const;
const SHAPE_TOOLS = ['rectangle', 'square', 'circle', 'oval', 'triangle'] as const;
/** Freihand-Werkzeuge: Snap to Grid nicht anwenden. */
const FREEHAND_TOOLS = ['pen', 'marker', 'eraser'] as const;
function isFreehandTool(tool: string): boolean {
  return (FREEHAND_TOOLS as readonly string[]).includes(tool);
}
type ShapeTool = (typeof SHAPE_TOOLS)[number];
type DrawingStroke = {
  id: string;
  tool: DrawTool | ShapeTool | string;
  color: string;
  width: number;
  alpha: number;
  points: Array<{ x: number; y: number }>;
  createdAt: string;
};

const BACKGROUND_PRESETS = [
  { label: 'Weiß', value: '#ffffff' },
  { label: 'Hellgrau', value: '#f1f5f9' },
  { label: 'Dunkel', value: '#1e293b' },
  { label: 'Schwarz', value: '#0f172a' },
] as const;
const DISPLAY_SPRUECHE = spruecheRaw
  .split(/\r?\n/)
  .map((line) => line.trim())
  .filter(Boolean);

type CompetencyToastOverlay = {
  id: string;
  mode: 'simple' | 'ranking';
  studentId: string;
  fullName: string;
  delta: 1 | -1;
  durationMs: number;
  previousScore?: number | null;
  newScore?: number | null;
  top5Before?: Array<{
    studentId: string;
    firstName: string;
    lastName: string;
    score: number;
  }>;
  top5After?: Array<{
    studentId: string;
    firstName: string;
    lastName: string;
    score: number;
  }>;
};

function rankingDisplayName(entry: { firstName?: string; lastName?: string; studentId?: string }) {
  const fullName = [String(entry.firstName || '').trim(), String(entry.lastName || '').trim()]
    .filter(Boolean)
    .join(' ');
  return fullName || String(entry.studentId || 'Schueler/in');
}

function buildCompetitionRankMap(
  list: Array<{ studentId: string; firstName: string; lastName: string; score: number }>
) {
  const rankById = new Map<string, number>();
  let prevScore: number | null = null;
  let prevRank = 0;
  list.forEach((row, index) => {
    const normalizedScore = Math.max(0, Number(row.score || 0));
    const currentRank = prevScore !== null && normalizedScore === prevScore ? prevRank : index + 1;
    rankById.set(String(row.studentId || `${row.firstName}-${row.lastName}`), currentRank);
    prevScore = normalizedScore;
    prevRank = currentRank;
  });
  return rankById;
}

function RankingToastCard({ entry }: { entry: CompetencyToastOverlay }) {
  const before = Math.max(0, Number(entry.previousScore ?? 0));
  const after = Math.max(0, Number(entry.newScore ?? before));
  const isPositive = entry.delta > 0;
  const [animatedScore, setAnimatedScore] = useState(before);
  const [animateRanks, setAnimateRanks] = useState(false);
  const ROW_HEIGHT = 24;
  const top5Before = (entry.top5Before || []).slice(0, 7);
  const top5After = (entry.top5After || []).slice(0, 7);

  useEffect(() => {
    setAnimatedScore(before);
    if (before === after) return;
    const direction = after > before ? 1 : -1;
    const interval = window.setInterval(() => {
      setAnimatedScore((prev) => {
        const next = prev + direction;
        if ((direction > 0 && next >= after) || (direction < 0 && next <= after)) {
          window.clearInterval(interval);
          return after;
        }
        return next;
      });
    }, 90);
    return () => window.clearInterval(interval);
  }, [before, after]);

  useEffect(() => {
    setAnimateRanks(false);
    const timer = window.setTimeout(() => setAnimateRanks(true), 35);
    return () => window.clearTimeout(timer);
  }, [entry.id]);

  const beforeById = new Map(
    top5Before.map((row, index) => [String(row.studentId || `${row.firstName}-${row.lastName}`), { ...row, index }])
  );
  const afterById = new Map(
    top5After.map((row, index) => [String(row.studentId || `${row.firstName}-${row.lastName}`), { ...row, index }])
  );
  const beforeRankById = buildCompetitionRankMap(top5Before);
  const afterRankById = buildCompetitionRankMap(top5After);
  const orderedIds = Array.from(
    new Set([
      ...top5Before.map((row) => String(row.studentId || `${row.firstName}-${row.lastName}`)),
      ...top5After.map((row) => String(row.studentId || `${row.firstName}-${row.lastName}`))
    ])
  );

  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-2xl backdrop-blur-sm ${
        isPositive
          ? 'border-violet-300/70 bg-violet-600/25 text-violet-50'
          : 'border-rose-400/60 bg-rose-600/25 text-rose-50'
      }`}
    >
      <div className="mb-1 flex items-center gap-2 text-sm">
        <span className={isPositive ? 'text-violet-200' : 'text-rose-200'}>🏆</span>
        <span className="font-semibold">Top 5 Ranking</span>
      </div>
      <div className="mb-2 text-xs">
        <span className="font-medium">{entry.fullName}</span>
        <span className="mx-1 rounded bg-white/15 px-1 py-0.5 font-bold">{isPositive ? '+1' : '-1'}</span>
        <span>
          {before} → <span className="font-extrabold">{animatedScore}</span> Plus
        </span>
      </div>
      <div className="relative text-[11px]" style={{ height: `${Math.max(top5Before.length, top5After.length) * ROW_HEIGHT}px` }}>
        {orderedIds.map((studentKey) => {
          const beforeRow = beforeById.get(studentKey);
          const afterRow = afterById.get(studentKey);
          const startIndex = beforeRow ? beforeRow.index : Math.min((afterRow?.index ?? 0) + 0.8, 4.8);
          const endIndex = afterRow ? afterRow.index : Math.max((beforeRow?.index ?? 0) - 0.8, -0.2);
          const currentIndex = animateRanks ? endIndex : startIndex;
          const currentTop = currentIndex * ROW_HEIGHT;
          const isTarget = Boolean(entry.studentId) && studentKey === entry.studentId;
          const visible = animateRanks ? Boolean(afterRow) : Boolean(beforeRow || afterRow);
          const labelSource = afterRow || beforeRow;
          if (!labelSource) return null;
          return (
            <div
              key={`${entry.id}:${studentKey}`}
              className={`absolute left-0 right-0 flex items-center justify-between rounded px-1.5 py-0.5 transition-all duration-700 ease-in-out ${
                isTarget ? 'bg-white/20 font-semibold' : 'bg-black/10'
              }`}
              style={{
                top: `${currentTop}px`,
                opacity: visible ? 1 : 0
              }}
            >
              <span className="truncate pr-2">
                {(afterRankById.get(studentKey) ?? beforeRankById.get(studentKey) ?? (afterRow?.index ?? beforeRow?.index ?? 0) + 1)}.{' '}
                {rankingDisplayName(labelSource)}
              </span>
              <span>{Math.max(0, Number((afterRow?.score ?? beforeRow?.score) || 0))}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatRemaining(deadlineMs: number, nowMs: number) {
  const delta = Math.round((Number(deadlineMs || 0) - nowMs) / 1000);
  const abs = Math.abs(delta);
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  const core = `${m}:${String(s).padStart(2, '0')}`;
  return delta < 0 ? `+${core}` : core;
}

function formatElapsedFromMs(startedAtMs: number, nowMs: number) {
  const safeStart = Math.max(0, Number(startedAtMs || 0));
  const diff = Math.max(0, nowMs - safeStart);
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  if (minutes > 0) {
    return `${minutes} Min`;
  }
  return `${seconds} Sek`;
}

function LernbueroDashboardView({
  dashboard,
  nowMs
}: {
  dashboard: NonNullable<NonNullable<DisplayStatus['connectedContext']>['lernbueroDashboard']>;
  nowMs: number;
}) {
  const students = Array.isArray(dashboard.students) ? dashboard.students : [];
  const top10 = Array.isArray(dashboard.top10) ? dashboard.top10 : [];
  const groups = Array.isArray(dashboard.groups) ? dashboard.groups : [];
  const absences = Array.isArray(dashboard.absences) ? dashboard.absences : [];
  const longestAbsences = [...absences]
    .sort((a, b) => Number(a.startedAtMs || 0) - Number(b.startedAtMs || 0))
    .slice(0, 5);
  return (
    <div className="absolute inset-0 z-[240] bg-slate-950 text-slate-100">
      <div className="grid h-full grid-cols-12 gap-4 p-5">
        <div className="col-span-8 flex min-h-0 flex-col rounded-2xl border border-slate-700 bg-slate-900/85 p-4">
          <div className="mb-3 flex items-end justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Lernbuero Panel A</p>
              <h2 className="text-2xl font-semibold text-white">{dashboard.sessionLabel || 'Aktive Sitzung'}</h2>
            </div>
            <div className="text-right text-xs text-slate-400">
              <p>{students.length} anwesend</p>
              <p>{absences.length} abgängig</p>
              <p>{groups.length} Gruppen</p>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-700">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/90 text-slate-300">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Vorname</th>
                  <th className="px-3 py-2 text-left">Klasse</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student) => (
                  <tr key={student.unid} className="border-t border-slate-800">
                    <td className="px-3 py-2">{student.lastName}</td>
                    <td className="px-3 py-2">{student.firstName}</td>
                    <td className="px-3 py-2 text-slate-400">{student.className || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="col-span-4 flex min-h-0 flex-col gap-4">
          <div className="rounded-2xl border border-slate-700 bg-slate-900/85 p-4">
            <h3 className="mb-2 text-lg font-semibold text-white">Top 10</h3>
            <div className="space-y-1.5 text-sm">
              {top10.length === 0 ? (
                <p className="text-slate-400">Keine Daten</p>
              ) : (
                top10.map((entry) => (
                  <div key={entry.unid} className="flex items-center justify-between rounded bg-slate-800/80 px-2 py-1.5">
                    <span className="truncate pr-2">
                      {entry.rank}. {entry.firstName} {entry.lastName}
                    </span>
                    <span className="font-semibold text-emerald-300">{Math.max(0, Number(entry.count || 0))}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="max-h-[28vh] overflow-auto rounded-2xl border border-slate-700 bg-slate-900/85 p-4">
            <h3 className="mb-2 text-lg font-semibold text-white">kurzzeitige Abwesenheiten</h3>
            <div className="space-y-1.5 text-sm">
              {longestAbsences.length === 0 ? (
                <p className="text-slate-400">Keine Abwesenheiten</p>
              ) : (
                longestAbsences.map((entry) => (
                  <div key={entry.unid} className="flex items-center justify-between rounded bg-amber-950/40 px-2 py-1.5">
                    <span className="truncate pr-2 text-amber-100">
                      {entry.firstName} {entry.lastName}
                      {entry.className ? ` (${entry.className})` : ''}
                    </span>
                    <span className="font-semibold text-amber-300">
                      seit {formatElapsedFromMs(Number(entry.startedAtMs || 0), nowMs)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto rounded-2xl border border-slate-700 bg-slate-900/85 p-4">
            <h3 className="mb-2 text-lg font-semibold text-white">Gruppenarbeit</h3>
            <div className="space-y-2">
              {groups.length === 0 ? (
                <p className="text-sm text-slate-400">Keine aktive Gruppenarbeit</p>
              ) : (
                groups.map((group) => {
                  const remaining = formatRemaining(Number(group.deadlineMs || 0), nowMs);
                  const overdue = Number(group.deadlineMs || 0) <= nowMs;
                  return (
                    <div
                      key={group.id}
                      className={`rounded-lg border px-3 py-2 transition-shadow ${
                        overdue
                          ? 'border-red-500/60 bg-red-950/30 shadow-[0_0_0_1px_rgba(239,68,68,0.2),0_0_18px_rgba(239,68,68,0.2)] animate-pulse'
                          : 'border-slate-700 bg-slate-800/70'
                      }`}
                    >
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="font-semibold">Gruppe {group.id}</span>
                        <span className={overdue ? 'font-semibold text-red-300' : 'font-semibold text-emerald-300'}>{remaining}</span>
                      </div>
                      <p className="truncate text-xs text-slate-300">
                        {(group.members || []).map((m) => `${m.firstName} ${m.lastName}`).join(', ') || 'Ohne Mitglieder'}
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function createStrokeId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `stroke_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Magnet-Snap: bei Rechenkaro Kreuzungen leichter treffen. Nicht zu stark. */
const GRID_SNAP_SPACING = 40;
const GRID_SNAP_MAX_DIST = 7;
function snapToGrid(x: number, y: number): { x: number; y: number } {
  const gx = Math.round(x / GRID_SNAP_SPACING) * GRID_SNAP_SPACING;
  const gy = Math.round(y / GRID_SNAP_SPACING) * GRID_SNAP_SPACING;
  if (Math.hypot(x - gx, y - gy) <= GRID_SNAP_MAX_DIST) return { x: gx, y: gy };
  return { x, y };
}

type NcBrowseItem = {
  path: string;
  name: string;
  isDirectory: boolean;
  mimeType?: string | null;
};

type NcBrowseApiResponse = {
  data?: { items?: NcBrowseItem[] };
};

type NcStatusApiResponse = {
  data?: { enabled?: boolean; adminConfigured?: boolean; userConfigured?: boolean };
};

type OdStatusApiResponse = {
  data?: { enabled?: boolean; adminConfigured?: boolean; userConfigured?: boolean };
};

export default function Display() {
  const [status, setStatus] = useState<DisplayStatus | null>(null);
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReleasing, setIsReleasing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [waitingPinInStatusBar, setWaitingPinInStatusBar] = useState(false);
  const [activeDrawTool, setActiveDrawTool] = useState<DrawTool | ShapeTool | null>('pen');
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [shapeMenuPosition, setShapeMenuPosition] = useState<{ left: number; bottom: number } | null>(null);
  const shapeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [activeDrawColor, setActiveDrawColor] = useState('#FFFFFF');
  const [drawWidth, setDrawWidth] = useState(4);
  const [backgroundColor, setBackgroundColor] = useState('#1e293b');
  const [showWritingLines, setShowWritingLines] = useState(false);
  const [showMathGrid, setShowMathGrid] = useState(false);
  const [showBackgroundMenu, setShowBackgroundMenu] = useState(false);
  const backgroundButtonRef = useRef<HTMLButtonElement | null>(null);
  const [backgroundMenuPosition, setBackgroundMenuPosition] = useState<{ left: number; bottom: number } | null>(null);
  // Dateien-Modal: User-Dateien für Display anzeigen
  const [showDateienModal, setShowDateienModal] = useState(false);
  const [dateienFolderStack, setDateienFolderStack] = useState<Array<{ id: number; name: string }>>([]);
  const [dateienContents, setDateienContents] = useState<{
    folders: Array<{ id: number; name: string }>;
    files: Array<{ file_id: number; original_name: string; mime_type?: string; media_type?: string }>;
  }>({ folders: [], files: [] });
  const [dateienLoading, setDateienLoading] = useState(false);
  const [dateienError, setDateienError] = useState<string | null>(null);
  const [dateienSource, setDateienSource] = useState<'lernrakete' | 'nextcloud' | 'onedrive'>('lernrakete');
  const [ncPathStack, setNcPathStack] = useState<string[]>([]);
  const [ncContents, setNcContents] = useState<{
    folders: Array<{ path: string; name: string }>;
    files: Array<{ path: string; original_name: string; mime_type?: string }>;
  }>({ folders: [], files: [] });
  const [lrAvailable, setLrAvailable] = useState(true);
  const [ncAvailable, setNcAvailable] = useState(false);
  const [odPathStack, setOdPathStack] = useState<string[]>([]);
  const [odContents, setOdContents] = useState<{
    folders: Array<{ path: string; name: string }>;
    files: Array<{ path: string; original_name: string; mime_type?: string }>;
  }>({ folders: [], files: [] });
  const [odAvailable, setOdAvailable] = useState(false);
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [textBoxDrawMode, setTextBoxDrawMode] = useState(false);
  const [textBoxDraft, setTextBoxDraft] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [geodreieck, setGeodreieck] = useState<GeodreieckState>(() => ({ ...DEFAULT_GEODREIECK }));
  const [calculator, setCalculator] = useState<CalculatorState>(() => ({ ...DEFAULT_CALCULATOR }));
  const [zirkel, setZirkel] = useState<ZirkelState>(() => ({ ...DEFAULT_ZIRKEL }));
  const [localDrawStrokes, setLocalDrawStrokes] = useState<
    Array<{
      id: string;
      tool: DrawTool | string;
      color: string;
      width: number;
      alpha: number;
      points: Array<{ x: number; y: number }>;
      createdAt: string;
    }>
  >([]);
  const [redoDrawStrokes, setRedoDrawStrokes] = useState<
    Array<{
      id: string;
      tool: DrawTool | string;
      color: string;
      width: number;
      alpha: number;
      points: Array<{ x: number; y: number }>;
      createdAt: string;
    }>
  >([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [zirkelArcPreview, setZirkelArcPreview] = useState<{
    cx: number;
    cy: number;
    radius: number;
    startDeg: number;
    endDeg: number;
  } | null>(null);
  const [mediaLayout, setMediaLayout] = useState<
    Record<string, { x: number; y: number; widthPx: number; z: number; rotationDeg: number; visible: boolean }>
  >({});
  const [locallyDismissedItemIds, setLocallyDismissedItemIds] = useState<string[]>([]);
  const [activeHandlesItemId, setActiveHandlesItemId] = useState<string | null>(null);
  const [handlesFading, setHandlesFading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{
    itemId: string;
    mode: 'move' | 'resize';
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
    startWidthPx: number;
    startRotationDeg: number;
    startHandleX: number;
    startHandleY: number;
    startDiagLength: number;
    startDiagAngle: number;
    // Latest computed position, applied via rAF to avoid React re-renders on every pointermove
    pendingX: number;
    pendingY: number;
    pendingWidth: number;
    pendingRotationDeg: number;
  } | null>(null);
  const dragRafPendingRef = useRef(false);
  const fadeTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const layoutSyncTimerRef = useRef<number | null>(null);
  const layoutSyncVersionRef = useRef(0);
  const mediaLayoutRef = useRef<Record<string, { x: number; y: number; widthPx: number; z: number; rotationDeg: number; visible: boolean }>>({});
  const pendingDefaultSyncRef = useRef(false);
  const lastDragEndRef = useRef(0);
  const [editingTextItemId, setEditingTextItemId] = useState<string | null>(null);
  const [pendingFocusTextItemId, setPendingFocusTextItemId] = useState<string | null>(null);
  const textItemRefs = useRef<Record<string, { titleEl: HTMLElement | null; bodyEl: HTMLElement | null }>>({});
  const textSyncTimersRef = useRef<Record<string, number | null>>({});
  const textBoxStartRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const deviceTokenRef = useRef<string | null>(null);
  const displayPinRef = useRef<string | null>(null);
  const lastConnectedSourceRef = useRef<string>('');
  const seenCompetencyToastIdsRef = useRef<Set<string>>(new Set());
  const competencyToastQueueRef = useRef<CompetencyToastOverlay[]>([]);
  const activeCompetencyToastIdRef = useRef<string | null>(null);
  const activeCompetencyToastTimeoutRef = useRef<number | null>(null);
  const competencyAudioContextRef = useRef<AudioContext | null>(null);
  const overlayPushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const liveCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const zirkelPreviewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafPendingRef = useRef(false);
  const drawingStrokesRef = useRef<
    Array<{
      id: string;
      tool: DrawTool | string;
      color: string;
      width: number;
      alpha: number;
      points: Array<{ x: number; y: number }>;
      createdAt: string;
    }>
  >([]);
  const drawingDirtyRef = useRef(false);
  const drawingSyncInFlightRef = useRef(false);
  const drawingAppendInFlightRef = useRef(false);
  const drawingNeedsFullSyncRef = useRef(false);
  const drawingAppendQueueRef = useRef<DrawingStroke[]>([]);
  const isDrawingRef = useRef(false);
  const liveStrokeRenderRef = useRef<{ strokeId: string; renderedPoints: number } | null>(null);
  const drawingCurrentStrokeRef = useRef<{
    id: string;
    tool: DrawTool | ShapeTool;
    color: string;
    width: number;
    alpha: number;
    points: Array<{ x: number; y: number }>;
    createdAt: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isHandMode = !activeDrawTool;

  // ── Page navigation ───────────────────────────────────────────────────────
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isPageSwitching, setIsPageSwitching] = useState(false);
  const [competencyToasts, setCompetencyToasts] = useState<CompetencyToastOverlay[]>([]);
  const [dashboardNowMs, setDashboardNowMs] = useState(() => Date.now());
  const dashboardServerClockRef = useRef<{ serverNowMs: number; syncedAtLocalMs: number } | null>(null);

  // Fixed logical canvas size – both Display and Mirror work in this space.
  const CANVAS_W = 1920;
  const CANVAS_H = 1080;
  const [canvasScale, setCanvasScale] = useState(1);
  const canvasScaleRef = useRef(1);
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);

  const displayTheme = useMemo(
    () => extractDisplayTheme(status?.connectedContext?.theme),
    [status?.connectedContext?.theme]
  );

  // ── Dateien: unterstützte Medientypen für Display (Bilder, Video, PDF) ─────
  const DISPLAY_SUPPORTED_MIMES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp',
    'video/mp4', 'video/quicktime', 'video/avi',
    'application/pdf', 'application/x-pdf'
  ];
  function isDisplaySupportedFile(mime: string | undefined): boolean {
    if (!mime) return false;
    const lower = mime.toLowerCase();
    if (lower.startsWith('image/') || lower.startsWith('video/') || lower === 'application/pdf' || lower === 'application/x-pdf') return true;
    return DISPLAY_SUPPORTED_MIMES.includes(lower);
  }
  function mimeToDisplayMediaType(mime: string | undefined): 'image' | 'video' | 'pdf' {
    if (!mime) return 'image';
    const lower = mime.toLowerCase();
    if (lower.startsWith('video/')) return 'video';
    if (lower === 'application/pdf' || lower === 'application/x-pdf') return 'pdf';
    return 'image';
  }

  const playCompetencyPling = useCallback(() => {
    try {
      const Ctx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const audioCtx = competencyAudioContextRef.current || new Ctx();
      competencyAudioContextRef.current = audioCtx;
      if (audioCtx.state === 'suspended') {
        void audioCtx.resume().catch(() => {});
      }

      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(900, audioCtx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1400, audioCtx.currentTime + 0.18);
      gainNode.gain.setValueAtTime(0.0001, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.08, audioCtx.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.2);
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.22);
    } catch {
      // Audio feedback is best-effort only.
    }
  }, []);

  const processNextCompetencyToast = useCallback(() => {
    if (activeCompetencyToastIdRef.current) {
      return;
    }
    const next = competencyToastQueueRef.current.shift();
    if (!next) {
      setCompetencyToasts([]);
      return;
    }

    activeCompetencyToastIdRef.current = next.id;
    setCompetencyToasts([next]);
    playCompetencyPling();

    if (activeCompetencyToastTimeoutRef.current) {
      window.clearTimeout(activeCompetencyToastTimeoutRef.current);
      activeCompetencyToastTimeoutRef.current = null;
    }

    const waitingRankingCount = competencyToastQueueRef.current.filter((entry) => entry.mode === 'ranking').length;
    const effectiveDurationMs =
      next.mode === 'ranking' && waitingRankingCount > 0
        ? Math.max(2600, Math.round(next.durationMs * 0.55))
        : next.durationMs;

    activeCompetencyToastTimeoutRef.current = window.setTimeout(() => {
      if (activeCompetencyToastIdRef.current === next.id) {
        activeCompetencyToastIdRef.current = null;
      }
      setCompetencyToasts([]);
      activeCompetencyToastTimeoutRef.current = null;
      processNextCompetencyToast();
    }, effectiveDurationMs);
  }, [playCompetencyPling]);

  useEffect(() => {
    if (!showDateienModal) return;
    setDateienError(null);
    setDateienLoading(true);
    if (!deviceToken || !status?.pin) {
      setDateienError('Verbindungsdaten fehlen. Bitte Display neu laden.');
      setDateienLoading(false);
      setDateienContents({ folders: [], files: [] });
      setNcContents({ folders: [], files: [] });
      setOdContents({ folders: [], files: [] });
      return;
    }
    const params = new URLSearchParams({ deviceToken, pin: status.pin });
    const load = async () => {
      try {
        const [storageRes, ncStatusRes, odStatusRes] = await Promise.all([
          api.get<{ data?: { lernraketeEnabled?: boolean; nextcloudEnabled?: boolean; onedriveEnabled?: boolean } }>(`/display/storage/providers?${params}`),
          api.get<NcStatusApiResponse>(`/display/nextcloud/status?${params}`),
          api.get<OdStatusApiResponse>(`/display/onedrive/status?${params}`)
        ]);
        const lrOk = storageRes.data?.data?.lernraketeEnabled !== false;
        const ncOk = !!(storageRes.data?.data?.nextcloudEnabled && ncStatusRes.data?.data?.enabled !== false && ncStatusRes.data?.data?.adminConfigured && ncStatusRes.data?.data?.userConfigured);
        const odOk = !!(storageRes.data?.data?.onedriveEnabled && odStatusRes.data?.data?.enabled !== false && odStatusRes.data?.data?.adminConfigured && odStatusRes.data?.data?.userConfigured);
        setLrAvailable(lrOk);
        setNcAvailable(ncOk);
        setOdAvailable(odOk);
        if (!lrOk && dateienSource === 'lernrakete') {
          setDateienSource(ncOk ? 'nextcloud' : odOk ? 'onedrive' : 'lernrakete');
        }
        if (dateienSource === 'onedrive') {
          if (!odOk) {
            setDateienError('OneDrive ist nicht verbunden. Bitte in den persönlichen Einstellungen mit Microsoft verbinden.');
            setOdContents({ folders: [], files: [] });
            return;
          }
          const odPath = odPathStack.length > 0 ? odPathStack[odPathStack.length - 1] : '';
          const browseRes = await api.get<NcBrowseApiResponse>(
            `/display/onedrive/browse?${params}${odPath ? `&path=${encodeURIComponent(odPath)}` : ''}`
          );
          const items = browseRes.data?.data?.items || [];
          setOdContents({
            folders: items.filter((i) => i.isDirectory).map((i) => ({ path: i.path, name: i.name })),
            files: items.filter((i) => !i.isDirectory).map((i) => ({
              path: i.path,
              original_name: i.name,
              mime_type: i.mimeType || undefined
            }))
          });
          return;
        }
        if (dateienSource === 'nextcloud') {
          if (!ncOk) {
            setDateienError('Nextcloud ist nicht verbunden. Bitte Zugangsdaten in den persönlichen Einstellungen hinterlegen.');
            setNcContents({ folders: [], files: [] });
            return;
          }
          const ncPath = ncPathStack.length > 0 ? ncPathStack[ncPathStack.length - 1] : '';
          const browseRes = await api.get<NcBrowseApiResponse>(
            `/display/nextcloud/browse?${params}${ncPath ? `&path=${encodeURIComponent(ncPath)}` : ''}`
          );
          const items = browseRes.data?.data?.items || [];
          setNcContents({
            folders: items.filter((i) => i.isDirectory).map((i) => ({ path: i.path, name: i.name })),
            files: items.filter((i) => !i.isDirectory).map((i) => ({
              path: i.path,
              original_name: i.name,
              mime_type: i.mimeType || undefined
            }))
          });
          return;
        }
        if (dateienFolderStack.length === 0) {
          const res = await api.get<{ folders: Array<{ id: number; name: string }>; files: Array<{ file_id: number; original_name: string; mime_type?: string; media_type?: string }> }>(`/display/dateien/root?${params}`);
          const data = res.data;
          setDateienContents({
            folders: Array.isArray(data.folders) ? data.folders : [],
            files: Array.isArray(data.files) ? data.files : []
          });
        } else {
          const top = dateienFolderStack[dateienFolderStack.length - 1];
          const res = await api.get<{ subfolders: Array<{ id: number; name: string }>; files: Array<{ file_id: number; original_name: string; mime_type?: string; media_type?: string }> }>(`/display/dateien/folders/${top.id}?${params}`);
          const data = res.data;
          setDateienContents({
            folders: Array.isArray(data.subfolders) ? data.subfolders : [],
            files: Array.isArray(data.files) ? data.files : []
          });
        }
      } catch (err: any) {
        const statusCode = err?.response?.status;
        const serverError = err?.response?.data?.error;
        const msg =
          statusCode === 401 || statusCode === 403 || serverError === 'Access denied'
            ? 'Bitte auf diesem Gerät anmelden, um deine Dateien zu nutzen.'
            : serverError || err?.message || 'Dateien konnten nicht geladen werden.';
        setDateienError(msg);
        setDateienContents({ folders: [], files: [] });
        setNcContents({ folders: [], files: [] });
        setOdContents({ folders: [], files: [] });
      } finally {
        setDateienLoading(false);
      }
    };
    load();
  }, [showDateienModal, dateienFolderStack, ncPathStack, odPathStack, dateienSource, deviceToken, status?.pin]);

  const openDateienModal = () => {
    setDateienFolderStack([]);
    setNcPathStack([]);
    setOdPathStack([]);
    setDateienSource('lernrakete');
    setShowDateienModal(true);
  };
  const navigateDateienFolder = (folder: { id: number; name: string }) => {
    setDateienFolderStack((prev) => [...prev, folder]);
  };
  const navigateDateienBack = () => {
    if (dateienSource === 'nextcloud') {
      setNcPathStack((prev) => (prev.length <= 1 ? [] : prev.slice(0, -1)));
      return;
    }
    if (dateienSource === 'onedrive') {
      setOdPathStack((prev) => (prev.length <= 1 ? [] : prev.slice(0, -1)));
      return;
    }
    setDateienFolderStack((prev) => (prev.length <= 1 ? [] : prev.slice(0, -1)));
  };
  const navigateNcFolder = (folder: { path: string; name: string }) => {
    setNcPathStack((prev) => [...prev, folder.path]);
  };
  const navigateOdFolder = (folder: { path: string; name: string }) => {
    setOdPathStack((prev) => [...prev, folder.path]);
  };
  const presentNcFile = async (file: { path: string; original_name: string; mime_type?: string }) => {
    if (!status?.pin || !deviceToken) return;
    const base = window.location.origin;
    const q = new URLSearchParams({ deviceToken, pin: status.pin, path: file.path });
    const mediaUrl = `${base}/api/display/nextcloud/files/view?${q}`;
    const mediaType = mimeToDisplayMediaType(file.mime_type);
    const pin = status.pin;
    try {
      if (mediaType === 'image') {
        await api.post('/display/device/present-image', { deviceToken, pin, imageUrl: mediaUrl, title: file.original_name });
      } else {
        await api.post('/display/device/present-media', {
          deviceToken,
          pin,
          mediaUrl,
          mediaType,
          mimeType: file.mime_type || '',
          title: file.original_name
        });
      }
      setShowDateienModal(false);
    } catch (err: any) {
      setDateienError(err?.response?.data?.error || err?.message || 'Datei konnte nicht auf dem Display angezeigt werden.');
    }
  };

  const presentOdFile = async (file: { path: string; original_name: string; mime_type?: string }) => {
    if (!status?.pin || !deviceToken) return;
    const base = window.location.origin;
    const q = new URLSearchParams({ deviceToken, pin: status.pin, path: file.path });
    const mediaUrl = `${base}/api/display/onedrive/files/view?${q}`;
    const mediaType = mimeToDisplayMediaType(file.mime_type);
    const pin = status.pin;
    try {
      if (mediaType === 'image') {
        await api.post('/display/device/present-image', { deviceToken, pin, imageUrl: mediaUrl, title: file.original_name });
      } else {
        await api.post('/display/device/present-media', {
          deviceToken,
          pin,
          mediaUrl,
          mediaType,
          mimeType: file.mime_type || '',
          title: file.original_name
        });
      }
      setShowDateienModal(false);
    } catch (err: any) {
      setDateienError(err?.response?.data?.error || err?.message || 'Datei konnte nicht auf dem Display angezeigt werden.');
    }
  };

  const presentDateienFile = async (file: { file_id: number; original_name: string; mime_type?: string }) => {
    if (!status?.pin || !deviceToken) return;
    const base = window.location.origin;
    const q = new URLSearchParams({ deviceToken, pin: status.pin });
    const mediaUrl = `${base}/api/display/dateien/files/${file.file_id}/view?${q}`;
    const mediaType = mimeToDisplayMediaType(file.mime_type);
    const pin = status.pin;
    try {
      if (mediaType === 'image') {
        await api.post('/display/device/present-image', {
          deviceToken,
          pin,
          imageUrl: mediaUrl,
          title: file.original_name
        });
      } else {
        await api.post('/display/device/present-media', {
          deviceToken,
          pin,
          mediaUrl,
          mediaType,
          mimeType: file.mime_type || '',
          title: file.original_name
        });
      }
      setShowDateienModal(false);
    } catch (err: any) {
      setDateienError(err?.response?.data?.error || err?.message || 'Datei konnte nicht auf dem Display angezeigt werden.');
    }
  };

  const presentYouTube = async () => {
    const url = youtubeUrl.trim();
    if (!url || !status?.pin || !deviceToken) return;
    const lower = url.toLowerCase();
    if (!lower.includes('youtube.com') && !lower.includes('youtu.be')) {
      setYoutubeError('Bitte eine gültige YouTube-URL eingeben.');
      return;
    }
    setYoutubeError(null);
    try {
      await api.post('/display/device/present-media', {
        deviceToken,
        pin: status.pin,
        mediaUrl: url,
        mediaType: 'youtube',
        title: 'YouTube-Video'
      });
      setShowYouTubeModal(false);
      setYoutubeUrl('');
    } catch (err: any) {
      setYoutubeError(err?.response?.data?.error || err?.message || 'Video konnte nicht angezeigt werden.');
    }
  };

  const pointerToCanvas = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * CANVAS_W;
    const y = ((event.clientY - rect.top) / rect.height) * CANVAS_H;
    return {
      x: Math.max(0, Math.min(CANVAS_W, x)),
      y: Math.max(0, Math.min(CANVAS_H, y)),
    };
  };


  const createTextItemFromDraft = useCallback(async (draft: { x: number; y: number; width: number; height: number }) => {
    const token = deviceTokenRef.current ?? deviceToken;
    if (!token || !status?.pin) return;
    try {
      const response = await api.post('/display/device/present-text', {
        deviceToken: token,
        pin: status.pin,
        text: 'Text eingeben…'
      });
      const itemId = String(response?.data?.data?.itemId || '');
      if (!itemId) return;
      const maxZ = Math.max(0, ...Object.values(mediaLayoutRef.current).map((l) => Number(l?.z || 0)));
      const widthPx = Math.max(180, Math.round(draft.width));
      const x = Math.round(draft.x + draft.width / 2 - CANVAS_W / 2);
      const y = Math.round(draft.y + draft.height / 2 - CANVAS_H / 2);
      await updateDisplayLayout({
        deviceToken: token,
        pin: status.pin,
        layouts: [{ itemId, x, y, widthPx, z: maxZ + 1, rotationDeg: 0 }]
      });
      setPendingFocusTextItemId(itemId);
    } catch {
      // best-effort
    }
  }, [deviceToken, status?.pin]);

  // ── Sync page index from server status ────────────────────────────────────
  useEffect(() => {
    const serverIdx = status?.activePageIndex ?? 0;
    const serverTotal = status?.totalPages ?? 1;
    setTotalPages(serverTotal);
    if (serverIdx !== activePageIndex) {
      // Page changed externally (teacher switched from Mirror) → reset local drawing
      setActivePageIndex(serverIdx);
      setLocalDrawStrokes([]);
      setMediaLayout({});
      drawingStrokesRef.current = [];
      drawingDirtyRef.current = false;
      drawingSyncInFlightRef.current = false;
      drawingAppendInFlightRef.current = false;
      drawingNeedsFullSyncRef.current = false;
      drawingAppendQueueRef.current = [];
      liveStrokeRenderRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.activePageIndex, status?.totalPages]);
  const mediaItems = useMemo(() => {
    if (status?.status !== 'connected') {
      return [] as Array<
        | { id: string; type: 'image'; imageUrl: string; title: string }
        | { id: string; type: 'text'; text: string; title: string }
        | { id: string; type: 'media'; mediaUrl: string; mediaType: 'image' | 'video' | 'pdf' | 'youtube'; mimeType: string; title: string }
      >;
    }
    const list = Array.isArray(status?.connectedContext?.contentItems) ? status.connectedContext.contentItems : [];
    const fromList = list
      .map((item) => {
        if (item?.type === 'text') {
          return {
            id: String(item?.id || ''),
            type: 'text' as const,
            text: String(item?.text || ''),
            title: String(item?.title || '')
          };
        }
        if (item?.type === 'media') {
          const inferredMediaType =
            item?.mediaType === 'video' || item?.mediaType === 'pdf' || item?.mediaType === 'youtube'
              ? item.mediaType
              : 'image';
          return {
            id: String(item?.id || ''),
            type: 'media' as const,
            mediaUrl: String(item?.mediaUrl || ''),
            mediaType: inferredMediaType as 'image' | 'video' | 'pdf' | 'youtube',
            mimeType: String(item?.mimeType || ''),
            title: String(item?.title || '')
          };
        }
        return {
          id: String(item?.id || ''),
          type: 'image' as const,
          imageUrl: String(item?.imageUrl || ''),
          title: String(item?.title || '')
        };
      })
      .filter(
        (item) =>
          item.id &&
          (item.type === 'text'
            ? Boolean((item as { text?: string }).text)
            : item.type === 'media'
            ? Boolean((item as { mediaUrl?: string }).mediaUrl)
            : Boolean((item as { imageUrl?: string }).imageUrl))
      );
    if (fromList.length > 0) {
      return fromList;
    }
    // Legacy single-item fallback must only run in non-paged contexts.
    if (status?.connectedContext?.hasPages) {
      return [];
    }
    const legacy = status?.connectedContext?.content;
    if (legacy?.type === 'text' && legacy.text) {
      return [
        {
          id: `legacy-text:${status?.connectedContext?.contentUpdatedAt || ''}`,
          type: 'text' as const,
          text: legacy.text,
          title: legacy.title || ''
        }
      ];
    }
    if (legacy?.type === 'image' && legacy.imageUrl) {
      return [
        {
          id: `legacy:${legacy.imageUrl}:${status?.connectedContext?.contentUpdatedAt || ''}`,
          type: 'image' as const,
          imageUrl: legacy.imageUrl,
          title: legacy.title || ''
        }
      ];
    }
    return [];
  }, [status?.status, status?.connectedContext?.contentItems, status?.connectedContext?.content, status?.connectedContext?.contentUpdatedAt]);
  const visibleMediaItems = useMemo(
    () => mediaItems.filter((item) => !locallyDismissedItemIds.includes(item.id)),
    [mediaItems, locallyDismissedItemIds]
  );

  const serverDrawingStrokes = useMemo(() => {
    const strokes = status?.connectedContext?.drawing?.strokes;
    if (!Array.isArray(strokes)) {
      return [];
    }
    return strokes
      .map((stroke) => ({
        id: String(stroke?.id || ''),
        tool: String(stroke?.tool || ''),
        color: String(stroke?.color || '#FFFFFF'),
        width: Number(stroke?.width || 2),
        alpha: Number(stroke?.alpha || 1),
        points: Array.isArray(stroke?.points)
          ? stroke.points
              .map((point) => ({ x: Number(point?.x || 0), y: Number(point?.y || 0) }))
              .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
          : [],
        createdAt: String(stroke?.createdAt || '')
      }))
      .filter((stroke) => stroke.id && stroke.points.length >= 1);
  }, [status?.connectedContext?.drawing?.strokes]);

  useEffect(() => {
    drawingStrokesRef.current = localDrawStrokes;
  }, [localDrawStrokes]);

  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  useEffect(() => {
    let mounted = true;

    const register = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const capabilities = detectCapabilities();
        const existingToken = capabilities.features.localStorage
          ? window.localStorage.getItem(DISPLAY_DEVICE_TOKEN_KEY) || undefined
          : undefined;
        const registered = await registerDisplay({
          deviceToken: existingToken,
          platform: capabilities.platform,
          userAgent: capabilities.userAgent,
          capabilities
        });
        if (capabilities.features.localStorage) {
          window.localStorage.setItem(DISPLAY_DEVICE_TOKEN_KEY, registered.deviceToken);
        }
        if (mounted) {
          setDeviceToken(registered.deviceToken);
          setStatus({
            pin: registered.pin,
            status: registered.status,
            transportMode: registered.transportMode,
            fallbackMode: registered.fallbackMode,
            connectedAt: registered.connectedAt,
            connectedByUserId: registered.connectedByUserId,
            connectedTeachingUnitId: registered.connectedTeachingUnitId,
            connectedContext: registered.connectedContext
          });
        }
      } catch (err: any) {
        if (mounted) {
          setError(err?.response?.data?.error || err?.message || 'Display konnte nicht registriert werden.');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    register();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (status?.status === 'connected') setWaitingPinInStatusBar(false);
  }, [status?.status]);

  useLayoutEffect(() => {
    if (!showShapeMenu || !shapeButtonRef.current) {
      setShapeMenuPosition(null);
      return;
    }
    const rect = shapeButtonRef.current.getBoundingClientRect();
    setShapeMenuPosition({ left: rect.left, bottom: window.innerHeight - rect.top + 4 });
  }, [showShapeMenu]);

  useLayoutEffect(() => {
    if (!showBackgroundMenu || !backgroundButtonRef.current) {
      setBackgroundMenuPosition(null);
      return;
    }
    const rect = backgroundButtonRef.current.getBoundingClientRect();
    setBackgroundMenuPosition({ left: rect.left, bottom: window.innerHeight - rect.top + 4 });
  }, [showBackgroundMenu]);

  useEffect(() => {
    const knownIds = new Set(mediaItems.map((item) => item.id));
    setLocallyDismissedItemIds((prev) => prev.filter((id) => knownIds.has(id)));
    // Skip layout sync while dragging or in grace period after drag
    if (isDragging || Date.now() - lastDragEndRef.current < 1500) return;
    const serverItems = Array.isArray(status?.connectedContext?.contentItems) ? status.connectedContext.contentItems : [];
    const serverLayoutMap: Record<string, { x: number; y: number; widthPx: number; z: number; rotationDeg: number }> = {};
    for (const si of serverItems) {
      if (si?.id && Number(si.layoutWidth) > 0) {
        serverLayoutMap[si.id] = {
          x: Number(si.layoutX) || 0,
          y: Number(si.layoutY) || 0,
          widthPx: Number(si.layoutWidth) || 520,
          z: Math.round(Number(si.layoutZ) || 0),
          rotationDeg: Number(si.layoutRotation) || 0
        };
      }
    }
    setMediaLayout((prev) => {
      const next: Record<string, { x: number; y: number; widthPx: number; z: number; rotationDeg: number; visible: boolean }> = {};
      let hasNewDefaults = false;
      mediaItems.forEach((item, index) => {
        const server = serverLayoutMap[item.id];
        // Server has authoritative layout → always use it (picks up Mirror changes)
        if (server && server.widthPx > 0) {
          const existing = prev[item.id];
          next[item.id] = { ...server, visible: existing?.visible ?? false };
          return;
        }
        // No real server layout – keep existing local state if present
        const existing = prev[item.id];
        if (existing) { next[item.id] = existing; return; }
        // Fallback: canvas-relative default (z. B. neu über Dateien-Picker – sofort sichtbar)
        hasNewDefaults = true;
        const initialWidth = Math.max(260, Math.min(CANVAS_W * 0.4, 760));
        next[item.id] = {
          x: index * 26,
          y: index * 26,
          widthPx: initialWidth,
          z: index + 1,
          rotationDeg: 0,
          visible: true
        };
      });
      if (hasNewDefaults) {
        pendingDefaultSyncRef.current = true;
        mediaLayoutRef.current = next;
      }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaItems, status?.connectedContext?.contentItems, isDragging]);

  useLayoutEffect(() => {
    mediaItems
      .filter((i) => i.type === 'text')
      .forEach((item) => {
        if (editingTextItemId === item.id) return;
        const refs = textItemRefs.current[item.id];
        if (!refs) return;
        if (refs.bodyEl) refs.bodyEl.innerText = (item as { text?: string }).text ?? '';
        if (refs.titleEl) refs.titleEl.innerText = (item as { title?: string }).title ?? '';
      });
  }, [mediaItems, editingTextItemId]);

  const scheduleTextItemUpdate = useCallback((itemId: string, patch: { text?: string; title?: string }) => {
    const token = deviceTokenRef.current ?? deviceToken;
    if (!token || !status?.pin) return;
    const key = String(itemId);
    const prev = textSyncTimersRef.current[key];
    if (prev) window.clearTimeout(prev);
    textSyncTimersRef.current[key] = window.setTimeout(() => {
      textSyncTimersRef.current[key] = null;
      deviceUpdateDisplayTextItem({ deviceToken: token, pin: status.pin!, itemId: key, ...patch }).catch(() => {});
    }, 350);
  }, [deviceToken, status?.pin]);

  const flushTextItemUpdate = useCallback((itemId: string, patch: { text?: string; title?: string }) => {
    const token = deviceTokenRef.current ?? deviceToken;
    if (!token || !status?.pin) return;
    const key = String(itemId);
    const prev = textSyncTimersRef.current[key];
    if (prev) {
      window.clearTimeout(prev);
      textSyncTimersRef.current[key] = null;
    }
    deviceUpdateDisplayTextItem({ deviceToken: token, pin: status.pin, itemId: key, ...patch }).catch(() => {});
  }, [deviceToken, status?.pin]);

  useEffect(() => {
    return () => {
      Object.values(textSyncTimersRef.current).forEach((timerId) => {
        if (timerId) window.clearTimeout(timerId);
      });
      textSyncTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!pendingFocusTextItemId) return;
    const refs = textItemRefs.current[pendingFocusTextItemId];
    if (!refs?.bodyEl) return;
    refs.bodyEl.focus();
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(refs.bodyEl);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    setPendingFocusTextItemId(null);
  }, [mediaItems, pendingFocusTextItemId]);

  useEffect(() => {
    mediaLayoutRef.current = mediaLayout;
    if (pendingDefaultSyncRef.current) {
      pendingDefaultSyncRef.current = false;
      scheduleLayoutSync();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaLayout]);

  useEffect(() => {
    deviceTokenRef.current = deviceToken;
  }, [deviceToken]);

  useEffect(() => {
    displayPinRef.current = status?.pin || null;
  }, [status?.pin]);

  // Sync overlay states from server status
  useEffect(() => {
    const g = status?.connectedContext?.geodreieck;
    const z = status?.connectedContext?.zirkel;
    const c = status?.connectedContext?.calculator;
    if (g && typeof g === 'object') {
      setGeodreieck((prev) => ({
        ...DEFAULT_GEODREIECK,
        ...prev,
        visible: Boolean(g.visible),
        x: Number(g.x) ?? prev.x,
        y: Number(g.y) ?? prev.y,
        rotation: Number(g.rotation) ?? prev.rotation,
        scale: Math.max(0.4, Math.min(6, Number(g.scale) || 1)),
      }));
    }
    if (z && typeof z === 'object') {
      setZirkel((prev) => ({
        ...DEFAULT_ZIRKEL,
        ...prev,
        visible: Boolean(z.visible),
        x: Number(z.x) ?? prev.x,
        y: Number(z.y) ?? prev.y,
        radius: Math.max(20, Math.min(500, Number(z.radius) || 120)),
        legLength: Math.max(LEG_LENGTH_MIN, Math.min(LEG_LENGTH_MAX, Number(z.legLength) || 90)),
      }));
    }
    if (c && typeof c === 'object') {
      setCalculator((prev) => ({
        ...DEFAULT_CALCULATOR,
        ...prev,
        visible: Boolean(c.visible),
        x: Number(c.x) ?? prev.x,
        y: Number(c.y) ?? prev.y
      }));
    }
  }, [status?.connectedContext?.geodreieck, status?.connectedContext?.zirkel, status?.connectedContext?.calculator]);

  // Push overlay state to server (debounced)
  useEffect(() => {
    const token = deviceTokenRef.current ?? deviceToken;
    if (!token || !status?.pin || status?.status !== 'connected') return;
    if (overlayPushTimeoutRef.current) clearTimeout(overlayPushTimeoutRef.current);
    overlayPushTimeoutRef.current = setTimeout(() => {
      overlayPushTimeoutRef.current = null;
      updateDisplayOverlays({
        deviceToken: token,
        pin: status.pin,
        geodreieck: { visible: geodreieck.visible, x: geodreieck.x, y: geodreieck.y, rotation: geodreieck.rotation, scale: geodreieck.scale },
        zirkel: { visible: zirkel.visible, x: zirkel.x, y: zirkel.y, radius: zirkel.radius, legLength: zirkel.legLength ?? DEFAULT_ZIRKEL.legLength },
        calculator: { visible: calculator.visible, x: calculator.x, y: calculator.y },
      }).catch(() => { /* best-effort */ });
    }, 400);
    return () => {
      if (overlayPushTimeoutRef.current) clearTimeout(overlayPushTimeoutRef.current);
    };
  }, [deviceToken, status?.pin, status?.status, geodreieck.visible, geodreieck.x, geodreieck.y, geodreieck.rotation, geodreieck.scale, zirkel.visible, zirkel.x, zirkel.y, zirkel.radius, zirkel.legLength, calculator.visible, calculator.x, calculator.y]);

  useEffect(() => {
    if (isDrawing) return;
    if (
      drawingDirtyRef.current ||
      drawingSyncInFlightRef.current ||
      drawingAppendInFlightRef.current ||
      drawingAppendQueueRef.current.length > 0
    ) return;

    // Server is authoritative once local writes are acknowledged.
    // This ensures undo/clear/page-switch from the connected side stay 1:1.
    setLocalDrawStrokes(() => {
      const next = serverDrawingStrokes as typeof localDrawStrokes;
      drawingStrokesRef.current = next;
      return next;
    });
  }, [serverDrawingStrokes, isDrawing]);

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) {
        window.clearTimeout(fadeTimerRef.current);
      }
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
      }
      if (layoutSyncTimerRef.current) {
        window.clearTimeout(layoutSyncTimerRef.current);
      }
      if (activeCompetencyToastTimeoutRef.current) {
        window.clearTimeout(activeCompetencyToastTimeoutRef.current);
        activeCompetencyToastTimeoutRef.current = null;
      }
      competencyToastQueueRef.current = [];
      activeCompetencyToastIdRef.current = null;
      if (competencyAudioContextRef.current) {
        void competencyAudioContextRef.current.close().catch(() => {});
        competencyAudioContextRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = deviceTokenRef.current;
      const pin = displayPinRef.current;
      const currentLayout = mediaLayoutRef.current;
      if (!token || !pin || Object.keys(currentLayout).length === 0) return;
      const layouts = Object.entries(currentLayout).map(([itemId, l]) => ({
        itemId,
        x: Math.round(l.x * 100) / 100,
        y: Math.round(l.y * 100) / 100,
        widthPx: Math.round(l.widthPx * 100) / 100,
        z: Math.round(l.z),
        rotationDeg: Math.round((l.rotationDeg || 0) * 10) / 10
      }));
      const apiBase = import.meta.env.DEV ? '/api' : `${window.location.origin}/api`;
      try {
        navigator.sendBeacon(
          `${apiBase}/display/update-layout`,
          new Blob([JSON.stringify({ deviceToken: token, pin, layouts })], { type: 'application/json' })
        );
      } catch { /* best-effort */ }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      const s = canvasScaleRef.current || 1;
      const dx = (event.clientX - drag.startX) / s;
      const dy = (event.clientY - drag.startY) / s;

      // Store latest computed values without triggering a React re-render yet
      if (drag.mode === 'move') {
        drag.pendingX = drag.startOffsetX + dx;
        drag.pendingY = drag.startOffsetY + dy;
      } else {
        const pX = drag.startHandleX + dx;
        const pY = drag.startHandleY + dy;
        const vX = pX - drag.startOffsetX;
        const vY = pY - drag.startOffsetY;
        const rawAngle = Math.atan2(vY, vX);
        let angleDelta = rawAngle - drag.startDiagAngle;
        while (angleDelta > Math.PI) angleDelta -= Math.PI * 2;
        while (angleDelta < -Math.PI) angleDelta += Math.PI * 2;
        const rawScale = Math.hypot(vX, vY) / drag.startDiagLength;
        const maxWidth = CANVAS_W * 0.96;
        drag.pendingWidth = Math.max(180, Math.min(maxWidth, drag.startWidthPx * Math.max(0.25, rawScale)));
        drag.pendingRotationDeg = drag.startRotationDeg + (angleDelta * 180) / Math.PI;
        drag.pendingX = drag.startOffsetX;
        drag.pendingY = drag.startOffsetY;
      }

      // Single rAF-throttled state update per frame – prevents React re-render storm
      if (!dragRafPendingRef.current) {
        dragRafPendingRef.current = true;
        requestAnimationFrame(() => {
          dragRafPendingRef.current = false;
          const d = dragStateRef.current;
          if (!d) return;
          if (d.mode === 'move') {
            setMediaLayout((prev) => ({
              ...prev,
              [d.itemId]: {
                ...(prev[d.itemId] || { x: 0, y: 0, widthPx: 520, z: 1, rotationDeg: 0, visible: true }),
                x: d.pendingX,
                y: d.pendingY
              }
            }));
          } else {
            setMediaLayout((prev) => ({
              ...prev,
              [d.itemId]: {
                ...(prev[d.itemId] || { x: 0, y: 0, widthPx: 520, z: 1, rotationDeg: 0, visible: true }),
                widthPx: d.pendingWidth,
                rotationDeg: Math.round(d.pendingRotationDeg * 10) / 10,
                x: d.pendingX,
                y: d.pendingY
              }
            }));
          }
        });
      }
    };
    const handlePointerUp = () => {
      dragRafPendingRef.current = false;
      if (dragStateRef.current) {
        // Apply the final pending position synchronously before clearing drag state
        const d = dragStateRef.current;
        if (d.mode === 'move') {
          setMediaLayout((prev) => ({
            ...prev,
            [d.itemId]: {
              ...(prev[d.itemId] || { x: 0, y: 0, widthPx: 520, z: 1, rotationDeg: 0, visible: true }),
              x: d.pendingX,
              y: d.pendingY
            }
          }));
        } else {
          setMediaLayout((prev) => ({
            ...prev,
            [d.itemId]: {
              ...(prev[d.itemId] || { x: 0, y: 0, widthPx: 520, z: 1, rotationDeg: 0, visible: true }),
              widthPx: d.pendingWidth,
              rotationDeg: Math.round(d.pendingRotationDeg * 10) / 10,
              x: d.pendingX,
              y: d.pendingY
            }
          }));
        }
        scheduleLayoutSync();
      }
      dragStateRef.current = null;
      setIsDragging(false);
      lastDragEndRef.current = Date.now();
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    // Also handle pointercancel so drag state is always cleaned up
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, []);

  useEffect(() => {
    if (!status?.pin) {
      return;
    }

    const source = String(status?.connectedContext?.source || '').toLowerCase();
    const pollingMs = source === 'live-lesson' ? 500 : 2000;
    const interval = window.setInterval(async () => {
      if (
        isDrawingRef.current ||
        drawingDirtyRef.current ||
        drawingSyncInFlightRef.current ||
        drawingAppendInFlightRef.current
      ) {
        return;
      }
      try {
        const next = await fetchDisplayStatus(status.pin);
        setStatus(next);
      } catch (error) {
        // polling ist best-effort, damit alte Browser nicht dauerhaft Fehler zeigen
      }
    }, pollingMs);

    return () => {
      window.clearInterval(interval);
    };
  }, [status?.pin, status?.connectedContext?.source]);

  useEffect(() => {
    const incoming = Array.isArray(status?.connectedContext?.competencyToasts)
      ? status.connectedContext.competencyToasts
      : [];
    if (!incoming.length) {
      return;
    }

    const seen = seenCompetencyToastIdsRef.current;
    const currentlyKnown = new Set<string>();
    incoming.forEach((item) => {
      const id = String(item?.id || '').trim();
      if (id) currentlyKnown.add(id);
    });

    incoming.forEach((item) => {
      const eventId = String(item?.id || '').trim();
      if (!eventId || seen.has(eventId)) return;

      seen.add(eventId);
      const firstName = String(item?.firstName || '').trim();
      const lastName = String(item?.lastName || '').trim();
      const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'Schueler/in';
      const studentId = String(item?.studentId || '').trim();
      const delta: 1 | -1 = Number(item?.delta) < 0 ? -1 : 1;
      const mode: 'simple' | 'ranking' = String(item?.mode || '').toLowerCase() === 'ranking' ? 'ranking' : 'simple';
      const localId = `overlay:${eventId}`;
      const toastDurationMs = mode === 'ranking' ? 7600 : 5200;
      const top5After = Array.isArray(item?.top5After)
        ? item.top5After.map((entry) => ({
            studentId: String(entry?.studentId || ''),
            firstName: String(entry?.firstName || ''),
            lastName: String(entry?.lastName || ''),
            score: Math.max(0, Number(entry?.score || 0))
          }))
        : [];
      const top5Before = Array.isArray(item?.top5Before)
        ? item.top5Before.map((entry) => ({
            studentId: String(entry?.studentId || ''),
            firstName: String(entry?.firstName || ''),
            lastName: String(entry?.lastName || ''),
            score: Math.max(0, Number(entry?.score || 0))
          }))
        : [];

      competencyToastQueueRef.current.push({
        id: localId,
        mode,
        studentId,
        fullName,
        delta,
        durationMs: toastDurationMs,
        previousScore: Number.isFinite(Number(item?.previousScore)) ? Number(item?.previousScore) : null,
        newScore: Number.isFinite(Number(item?.newScore)) ? Number(item?.newScore) : null,
        top5Before,
        top5After
      });
      if (competencyToastQueueRef.current.length > 20) {
        competencyToastQueueRef.current = competencyToastQueueRef.current.slice(-20);
      }
      processNextCompetencyToast();
    });

    if (seen.size > 500) {
      seenCompetencyToastIdsRef.current = currentlyKnown;
    }
  }, [status?.connectedContext?.competencyToasts, processNextCompetencyToast]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    handleFullscreenChange();
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const showCanvas = status?.status === 'connected' || waitingPinInStatusBar;
  const lernbueroDashboard = status?.connectedContext?.lernbueroDashboard || null;
  const activeDisplaySource = String(status?.connectedContext?.source || '').toLowerCase();
  const isUnterrichtenSource = activeDisplaySource.startsWith('unterrichten');
  const isLiveLessonSource = activeDisplaySource === 'live-lesson';
  const liveLessonOverlays = status?.connectedContext?.liveLessonOverlays || null;
  const liveLessonBoardState = status?.connectedContext?.boardDisplayState || null;
  const showLernbueroDashboard =
    status?.status === 'connected' &&
    Boolean(lernbueroDashboard?.enabled) &&
    !isUnterrichtenSource;
  const showLiveLessonBoard = isLiveLessonSource && !showLernbueroDashboard && Boolean(liveLessonBoardState?.enabled);
  useEffect(() => {
    const incomingServerNow = Number(lernbueroDashboard?.serverNowMs || 0);
    if (incomingServerNow > 0) {
      dashboardServerClockRef.current = {
        serverNowMs: incomingServerNow,
        syncedAtLocalMs: Date.now()
      };
      setDashboardNowMs(incomingServerNow);
    }
  }, [lernbueroDashboard?.serverNowMs]);
  useEffect(() => {
    const id = window.setInterval(() => {
      const sync = dashboardServerClockRef.current;
      if (sync) {
        setDashboardNowMs(sync.serverNowMs + (Date.now() - sync.syncedAtLocalMs));
      } else {
        setDashboardNowMs(Date.now());
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (status?.status !== 'connected') return;
    if (!activeDisplaySource) return;
    const previous = lastConnectedSourceRef.current;
    if (!previous) {
      lastConnectedSourceRef.current = activeDisplaySource;
      return;
    }
    if (previous !== activeDisplaySource) {
      window.location.reload();
      return;
    }
    lastConnectedSourceRef.current = activeDisplaySource;
  }, [status?.status, activeDisplaySource]);
  const hasNoDisplayContent = visibleMediaItems.length === 0 && localDrawStrokes.length === 0;
  const waitingQuote = useMemo(() => {
    if (!hasNoDisplayContent || DISPLAY_SPRUECHE.length === 0) return '';
    const randomIndex = Math.floor(Math.random() * DISPLAY_SPRUECHE.length);
    return DISPLAY_SPRUECHE[randomIndex] || '';
  }, [hasNoDisplayContent, status?.pin]);
  useEffect(() => {
    if (!showCanvas) return;
    const baseDpr = window.devicePixelRatio || 1;
    const committedDpr = Math.min(baseDpr, 2);
    const liveDpr = Math.min(baseDpr, 1.25);
    const applyCanvasSize = (canvas: HTMLCanvasElement | null, dpr: number) => {
      if (!canvas) return;
      canvas.width = Math.floor(CANVAS_W * dpr);
      canvas.height = Math.floor(CANVAS_H * dpr);
      canvas.style.width = `${CANVAS_W}px`;
      canvas.style.height = `${CANVAS_H}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    applyCanvasSize(drawingCanvasRef.current, committedDpr);
    applyCanvasSize(liveCanvasRef.current, liveDpr);
    if (zirkel.visible) applyCanvasSize(zirkelPreviewCanvasRef.current, liveDpr);
  }, [showCanvas, zirkel.visible]);

  // Track how much the 1920×1080 canvas needs to be scaled to fit the viewport.
  useEffect(() => {
    const measure = () => {
      const s = Math.min(window.innerWidth / CANVAS_W, window.innerHeight / CANVAS_H);
      canvasScaleRef.current = s;
      setCanvasScale(s);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draws a stroke with Bezier smoothing onto the given context (incl. Grundformen).
  const drawStrokeSmooth = (
    ctx: CanvasRenderingContext2D,
    stroke: { tool: string; color: string; width: number; alpha: number; points: Array<{ x: number; y: number }> }
  ) => {
    const pts = stroke.points;
    if (!pts.length) return;
    const isEraser = stroke.tool === 'eraser';
    ctx.save();
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    ctx.strokeStyle = isEraser ? 'rgba(0,0,0,1)' : stroke.color;
    ctx.globalAlpha = Math.max(0, Math.min(1, stroke.alpha ?? 1));
    ctx.lineWidth = stroke.width || 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (pts.length >= 2 && SHAPE_TOOLS.includes(stroke.tool as ShapeTool)) {
      const x0 = pts[0].x;
      const y0 = pts[0].y;
      const x1 = pts[1].x;
      const y1 = pts[1].y;
      const left = Math.min(x0, x1);
      const top = Math.min(y0, y1);
      const w = Math.abs(x1 - x0);
      const h = Math.abs(y1 - y0);
      if (stroke.tool === 'rectangle') {
        ctx.strokeRect(left, top, w || 1, h || 1);
      } else if (stroke.tool === 'square') {
        const s = Math.max(w, h, 1);
        const sx = (x0 + x1) / 2 - s / 2;
        const sy = (y0 + y1) / 2 - s / 2;
        ctx.strokeRect(sx, sy, s, s);
      } else if (stroke.tool === 'circle') {
        const r = Math.hypot(x1 - x0, y1 - y0) || 1;
        ctx.beginPath();
        ctx.arc(x0, y0, r, 0, Math.PI * 2);
        ctx.stroke();
      } else if (stroke.tool === 'oval') {
        const cx = (x0 + x1) / 2;
        const cy = (y0 + y1) / 2;
        const rx = Math.max(w / 2, 1);
        const ry = Math.max(h / 2, 1);
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      } else if (stroke.tool === 'triangle') {
        const mx = (x0 + x1) / 2;
        const baseLen = Math.hypot(x1 - x0, y1 - y0) || 1;
        const peakY = Math.min(y0, y1) - baseLen;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.lineTo(mx, peakY);
        ctx.closePath();
        ctx.stroke();
      }
    } else if ((stroke.tool === 'line' || stroke.tool === 'line-dashed' || stroke.tool === 'line-ruler') && pts.length >= 2) {
      const isDashed = stroke.tool === 'line-dashed';
      const isRuler = stroke.tool === 'line-ruler';
      if (isDashed) ctx.setLineDash([8, 6]);
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      if (pts.length === 2) {
        ctx.lineTo(pts[1].x, pts[1].y);
      } else {
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      }
      ctx.stroke();
      if (isDashed) ctx.setLineDash([]);
      if (isRuler && pts.length >= 2) {
        const x0 = pts[0].x, y0 = pts[0].y, x1 = pts[pts.length - 1].x, y1 = pts[pts.length - 1].y;
        const dx = x1 - x0, dy = y1 - y0;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len;
        const perpX = -uy, perpY = ux;
        const tickSpacing = 40;
        const tickLen = 8;
        for (let d = 0; d <= len; d += tickSpacing) {
          const t = d / len;
          const cx = x0 + t * dx, cy = y0 + t * dy;
          ctx.beginPath();
          ctx.moveTo(cx - perpX * tickLen, cy - perpY * tickLen);
          ctx.lineTo(cx + perpX * tickLen, cy + perpY * tickLen);
          ctx.stroke();
        }
      }
    } else if (pts.length === 1) {
      ctx.beginPath();
      ctx.arc(pts[0].x, pts[0].y, Math.max(1, (stroke.width || 2) / 2), 0, Math.PI * 2);
      ctx.fillStyle = isEraser ? 'rgba(0,0,0,1)' : stroke.color;
      ctx.fill();
    } else if (pts.length === 2) {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      ctx.lineTo(pts[1].x, pts[1].y);
      ctx.stroke();
    } else {
      // Midpoint quadratic Bezier for smooth curves between sparse points
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length - 1; i++) {
        const midX = (pts[i].x + pts[i + 1].x) / 2;
        const midY = (pts[i].y + pts[i + 1].y) / 2;
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, midX, midY);
      }
      ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
      ctx.stroke();
    }
    ctx.restore();
  };

  const drawBackgroundAndGuides = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const dpr = window.devicePixelRatio || 1;
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      const lineColor = luminance < 0.5 ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)';
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1;
      if (showWritingLines) {
        const lineSpacing = 48;
        for (let y = 0; y <= CANVAS_H; y += lineSpacing) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(CANVAS_W, y);
          ctx.stroke();
        }
      }
      if (showMathGrid) {
        const gridSpacing = 40;
        for (let x = 0; x <= CANVAS_W; x += gridSpacing) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, CANVAS_H);
          ctx.stroke();
        }
        for (let y = 0; y <= CANVAS_H; y += gridSpacing) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(CANVAS_W, y);
          ctx.stroke();
        }
      }
      ctx.restore();
    },
    [backgroundColor, showWritingLines, showMathGrid]
  );

  // Redraws all committed strokes onto the committed canvas (used for undo/redo/clear/server-sync).
  useEffect(() => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawBackgroundAndGuides(ctx);
    localDrawStrokes.forEach((stroke) => drawStrokeSmooth(ctx, stroke));
  }, [localDrawStrokes, backgroundColor, showWritingLines, showMathGrid, drawBackgroundAndGuides]);

  // Zirkel: Live-Vorschau des Kreisbogens auf eigenem Canvas (nicht den Stift-Live-Canvas überschreiben).
  useEffect(() => {
    const canvas = zirkelPreviewCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    if (zirkelArcPreview) {
      const points = arcPoints(
        zirkelArcPreview.cx,
        zirkelArcPreview.cy,
        zirkelArcPreview.radius,
        zirkelArcPreview.startDeg,
        zirkelArcPreview.endDeg
      );
      if (points.length >= 2) {
        drawStrokeSmooth(ctx, {
          tool: 'line',
          color: activeDrawColor,
          width: drawWidth,
          alpha: 1,
          points,
        });
      }
    }
  }, [zirkelArcPreview, activeDrawColor, drawWidth]);

  useEffect(() => {
    const flush = async () => {
      if (!deviceToken || !status?.pin) {
        return;
      }
      if (!drawingDirtyRef.current) {
        return;
      }
      if (drawingSyncInFlightRef.current || drawingAppendInFlightRef.current) {
        return;
      }

      if (!drawingNeedsFullSyncRef.current && drawingAppendQueueRef.current.length > 0) {
        const pending = drawingAppendQueueRef.current.slice(0, 24);
        drawingAppendInFlightRef.current = true;
        try {
          await appendDisplayDrawingStrokes({
            deviceToken,
            pin: status.pin,
            strokes: pending.map((stroke) => ({
              id: stroke.id,
              tool: String(stroke.tool),
              color: stroke.color,
              width: stroke.width,
              alpha: stroke.alpha,
              points: stroke.points,
              createdAt: stroke.createdAt
            }))
          });

          const currentQueue = drawingAppendQueueRef.current;
          const isSamePrefix =
            currentQueue.length >= pending.length &&
            pending.every((stroke, idx) => currentQueue[idx]?.id === stroke.id);
          if (isSamePrefix) {
            drawingAppendQueueRef.current = currentQueue.slice(pending.length);
          } else {
            const sentIds = new Set(pending.map((stroke) => stroke.id));
            drawingAppendQueueRef.current = currentQueue.filter((stroke) => !sentIds.has(stroke.id));
          }

          drawingDirtyRef.current = drawingNeedsFullSyncRef.current || drawingAppendQueueRef.current.length > 0;
        } catch (err) {
          // Fallback to authoritative replace sync if append failed.
          drawingNeedsFullSyncRef.current = true;
          drawingAppendQueueRef.current = [];
          drawingDirtyRef.current = true;
        } finally {
          drawingAppendInFlightRef.current = false;
        }
        return;
      }

      if (!drawingNeedsFullSyncRef.current) {
        drawingDirtyRef.current = drawingAppendQueueRef.current.length > 0;
        return;
      }

      const snapshot = drawingStrokesRef.current;
      const snapshotLastId = snapshot.length ? snapshot[snapshot.length - 1].id : '__empty__';
      const snapshotLength = snapshot.length;
      drawingSyncInFlightRef.current = true;
      try {
        await replaceDisplayDrawingStrokes({
          deviceToken,
          pin: status.pin,
          strokes: snapshot.map((stroke) => ({
            id: stroke.id,
            tool: String(stroke.tool),
            color: stroke.color,
            width: stroke.width,
            alpha: stroke.alpha,
            points: stroke.points,
            createdAt: stroke.createdAt
          }))
        });
        const current = drawingStrokesRef.current;
        const currentLastId = current.length ? current[current.length - 1].id : '__empty__';
        const unchangedSinceSend = current.length === snapshotLength && currentLastId === snapshotLastId;
        // Only mark clean if no newer local stroke was created during the request.
        drawingNeedsFullSyncRef.current = !unchangedSinceSend;
        drawingDirtyRef.current = drawingNeedsFullSyncRef.current || drawingAppendQueueRef.current.length > 0;
      } catch (err) {
        drawingNeedsFullSyncRef.current = true;
        drawingDirtyRef.current = true;
      } finally {
        drawingSyncInFlightRef.current = false;
      }
    };

    const interval = window.setInterval(flush, 350);
    return () => {
      window.clearInterval(interval);
    };
  }, [deviceToken, status?.pin]);

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenEnabled) {
        setError('Vollbild wird von diesem Browser nicht unterstützt.');
        return;
      }
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (err: any) {
      setError(err?.message || 'Vollbild konnte nicht aktiviert werden.');
    }
  };

  const handleReleaseConnection = async () => {
    if (!deviceToken || !status?.pin || status.status !== 'connected') {
      return;
    }
    const confirmed = window.confirm(`Verbindung für Display ${status.pin} freigeben?`);
    if (!confirmed) {
      return;
    }
    setIsReleasing(true);
    try {
      const next = await releaseDisplayConnection({ deviceToken, pin: status.pin });
      setStatus((prev) =>
        prev
          ? {
              ...prev,
              status: next?.status || 'waiting',
              connectedAt: null,
              connectedByUserId: null,
              connectedTeachingUnitId: null,
              connectedContext: null
            }
          : prev
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Verbindung konnte nicht freigegeben werden.');
    } finally {
      setIsReleasing(false);
    }
  };

  const handleStartMediaDrag = (itemId: string, mode: 'move' | 'resize', event: React.PointerEvent<HTMLElement>) => {
    const layout = mediaLayout[itemId] || { x: 0, y: 0, widthPx: 520, rotationDeg: 0 };
    event.preventDefault();
    event.stopPropagation();
    const itemShell = (event.currentTarget as HTMLElement).closest('[data-media-item-shell="true"]') as HTMLElement | null;
    const contentEl = itemShell?.querySelector('[data-media-item-content="true"]') as HTMLElement | null;
    const measuredHeight = Math.max(60, Number(contentEl?.offsetHeight || Math.round(layout.widthPx * 0.56)));
    const startRotationDeg = mediaLayout[itemId]?.rotationDeg || 0;
    const startRotationRad = (startRotationDeg * Math.PI) / 180;
    const cos = Math.cos(startRotationRad);
    const sin = Math.sin(startRotationRad);
    const halfW = layout.widthPx / 2;
    const halfH = measuredHeight / 2;
    const diagDx = halfW * cos - halfH * sin;
    const diagDy = halfW * sin + halfH * cos;
    const startDiagLength = Math.max(1, Math.hypot(diagDx, diagDy));
    const startDiagAngle = Math.atan2(diagDy, diagDx);
    dragStateRef.current = {
      itemId,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: layout.x,
      startOffsetY: layout.y,
      startWidthPx: mediaLayout[itemId]?.widthPx || 520,
      startRotationDeg,
      startHandleX: layout.x + diagDx,
      startHandleY: layout.y + diagDy,
      startDiagLength,
      startDiagAngle,
      pendingX: layout.x,
      pendingY: layout.y,
      pendingWidth: mediaLayout[itemId]?.widthPx || 520,
      pendingRotationDeg: mediaLayout[itemId]?.rotationDeg || 0
    };
    // Pointer capture keeps tracking the finger even when it moves over other elements
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* best-effort */ }
    setIsDragging(true);
  };

  const activateHandles = (itemId: string) => {
    setActiveHandlesItemId(itemId);
    setHandlesFading(false);
    if (fadeTimerRef.current) {
      window.clearTimeout(fadeTimerRef.current);
    }
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
    }
    fadeTimerRef.current = window.setTimeout(() => {
      setHandlesFading(true);
    }, 2500);
    hideTimerRef.current = window.setTimeout(() => {
      setActiveHandlesItemId(null);
      setHandlesFading(false);
    }, 3000);
  };

  const moveLayer = (itemId: string, direction: 'up' | 'down') => {
    setMediaLayout((prev) => {
      const entries = Object.entries(prev).sort((a, b) => a[1].z - b[1].z);
      const idx = entries.findIndex(([id]) => id === itemId);
      if (idx < 0) {
        return prev;
      }
      const swapIdx = direction === 'up' ? idx + 1 : idx - 1;
      if (swapIdx < 0 || swapIdx >= entries.length) {
        return prev;
      }
      const swapId = entries[swapIdx][0];
      return {
        ...prev,
        [itemId]: { ...prev[itemId], z: prev[swapId].z },
        [swapId]: { ...prev[swapId], z: prev[itemId].z }
      };
    });
    scheduleLayoutSync();
  };

  const flushLayoutToServerRef = useRef(async () => {
    const token = deviceTokenRef.current;
    const pin = displayPinRef.current;
    if (!token || !pin) {
      console.warn('[Display flush] No token or pin', { token: !!token, pin: !!pin });
      return;
    }
    const currentLayout = mediaLayoutRef.current;
    const layouts = Object.entries(currentLayout).map(([itemId, l]) => ({
      itemId,
      x: Math.round(l.x * 100) / 100,
      y: Math.round(l.y * 100) / 100,
      widthPx: Math.round(l.widthPx * 100) / 100,
      z: Math.round(l.z),
      rotationDeg: Math.round((l.rotationDeg || 0) * 10) / 10
    }));
    if (layouts.length === 0) {
      console.warn('[Display flush] No layouts to send');
      return;
    }
    console.log('[Display flush] Sending', layouts.length, 'layouts:', JSON.stringify(layouts.slice(0, 2)));
    try {
      await updateDisplayLayout({ deviceToken: token, pin, layouts });
      console.log('[Display flush] Success');
    } catch (err) {
      console.warn('Layout sync failed:', err);
    }
  });

  const scheduleLayoutSync = () => {
    layoutSyncVersionRef.current += 1;
    if (layoutSyncTimerRef.current) {
      window.clearTimeout(layoutSyncTimerRef.current);
    }
    layoutSyncTimerRef.current = window.setTimeout(() => {
      flushLayoutToServerRef.current();
    }, 600);
  };


  const handleDismissMediaItem = async (itemId: string) => {
    setLocallyDismissedItemIds((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
    if (!deviceToken || !status?.pin || itemId.startsWith('legacy:')) {
      return;
    }
    try {
      await dismissDisplayItem({
        deviceToken,
        pin: status.pin,
        itemId
      });
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Objekt konnte nicht geschlossen werden.');
      setLocallyDismissedItemIds((prev) => prev.filter((id) => id !== itemId));
    }
  };

  const beginDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!activeDrawTool) return;
    const canvas = liveCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const s = canvasScaleRef.current || 1;
    let x = (event.clientX - rect.left) / s;
    let y = (event.clientY - rect.top) / s;
    if (showMathGrid && !isFreehandTool(activeDrawTool)) {
      const sn = snapToGrid(x, y);
      x = sn.x;
      y = sn.y;
    }
    const stroke =
      activeDrawTool === 'marker'
        ? {
            id: createStrokeId(),
            tool: activeDrawTool,
            color: activeDrawColor,
            width: Math.max(drawWidth * 2.2, 10),
            alpha: 0.35,
            points: [{ x, y }],
            createdAt: new Date().toISOString()
          }
        : activeDrawTool === 'eraser'
        ? {
            id: createStrokeId(),
            tool: activeDrawTool,
            color: '#000000',
            width: Math.max(drawWidth * 2.5, 12),
            alpha: 1,
            points: [{ x, y }],
            createdAt: new Date().toISOString()
          }
        : (LINE_TWO_POINT_TOOLS as readonly string[]).includes(activeDrawTool) || (SHAPE_TOOLS as readonly string[]).includes(activeDrawTool)
        ? {
            id: createStrokeId(),
            tool: activeDrawTool,
            color: activeDrawColor,
            width: drawWidth,
            alpha: 1,
            points: [{ x, y }, { x, y }],
            createdAt: new Date().toISOString()
          }
        : {
            id: createStrokeId(),
            tool: activeDrawTool,
            color: activeDrawColor,
            width: drawWidth,
            alpha: 1,
            points: [{ x, y }],
            createdAt: new Date().toISOString()
          };
    drawingCurrentStrokeRef.current = stroke;
    liveStrokeRenderRef.current = { strokeId: stroke.id, renderedPoints: 0 };
    setIsDrawing(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = drawingCurrentStrokeRef.current;
    if (!stroke) return;
    const liveCanvas = liveCanvasRef.current;
    if (!liveCanvas) return;

    const rect = liveCanvas.getBoundingClientRect();
    const s = canvasScaleRef.current || 1;

    if ((LINE_TWO_POINT_TOOLS as readonly string[]).includes(stroke.tool) || SHAPE_TOOLS.includes(stroke.tool as ShapeTool)) {
      let x = (event.clientX - rect.left) / s;
      let y = (event.clientY - rect.top) / s;
      if (showMathGrid) {
        const sn = snapToGrid(x, y);
        x = sn.x;
        y = sn.y;
      }
      if (stroke.points.length >= 2) {
        stroke.points[1] = { x, y };
      }
    } else {
      const events: Array<{ clientX: number; clientY: number }> =
        typeof event.nativeEvent.getCoalescedEvents === 'function'
          ? event.nativeEvent.getCoalescedEvents()
          : [event.nativeEvent];
      const snap = showMathGrid && !isFreehandTool(stroke.tool);
      for (const e of events) {
        let x = (e.clientX - rect.left) / s;
        let y = (e.clientY - rect.top) / s;
        if (snap) {
          const sn = snapToGrid(x, y);
          x = sn.x;
          y = sn.y;
        }
        stroke.points.push({ x, y });
      }
    }

    // Schedule a single rAF-throttled redraw of the live canvas – no React state update.
    if (!rafPendingRef.current) {
      rafPendingRef.current = true;
      requestAnimationFrame(() => {
        rafPendingRef.current = false;
        const live = liveCanvasRef.current;
        const liveCtx = live?.getContext('2d');
        const current = drawingCurrentStrokeRef.current;
        if (!liveCtx || !live || !current) return;
        const needsFullRedraw =
          (LINE_TWO_POINT_TOOLS as readonly string[]).includes(current.tool) ||
          SHAPE_TOOLS.includes(current.tool as ShapeTool);
        if (needsFullRedraw) {
          liveCtx.clearRect(0, 0, live.width, live.height);
          drawStrokeSmooth(liveCtx, current);
          liveStrokeRenderRef.current = { strokeId: current.id, renderedPoints: current.points.length };
          return;
        }

        const liveState = liveStrokeRenderRef.current;
        if (!liveState || liveState.strokeId !== current.id) {
          liveCtx.clearRect(0, 0, live.width, live.height);
          drawStrokeSmooth(liveCtx, current);
          liveStrokeRenderRef.current = { strokeId: current.id, renderedPoints: current.points.length };
          return;
        }

        if (current.points.length <= liveState.renderedPoints) return;
        const from = Math.max(0, liveState.renderedPoints - 1);
        drawStrokeSmooth(liveCtx, { ...current, points: current.points.slice(from) });
        liveStrokeRenderRef.current = { strokeId: current.id, renderedPoints: current.points.length };
      });
    }

  };

  const endDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = drawingCurrentStrokeRef.current;
    if (!stroke) return;
    drawingCurrentStrokeRef.current = null;
    rafPendingRef.current = false;
    liveStrokeRenderRef.current = null;

    // Clear the live canvas
    const live = liveCanvasRef.current;
    const liveCtx = live?.getContext('2d');
    if (liveCtx && live) liveCtx.clearRect(0, 0, live.width, live.height);

    // Burn the finished stroke onto the committed canvas directly (avoids full redraw)
    const committedCtx = drawingCanvasRef.current?.getContext('2d');
    if (committedCtx) drawStrokeSmooth(committedCtx, stroke);

    // Single state update per stroke (triggers server sync / undo stack, not redraw)
    setLocalDrawStrokes((prev) => {
      const next = [...prev, { ...stroke }];
      drawingStrokesRef.current = next;
      return next;
    });
    setRedoDrawStrokes([]);
    drawingAppendQueueRef.current.push({ ...stroke });
    drawingDirtyRef.current = true;
    setIsDrawing(false);
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const handleUndoDrawing = () => {
    setLocalDrawStrokes((prev) => {
      if (!prev.length) {
        return prev;
      }
      const next = [...prev];
      const removed = next.pop();
      if (removed) {
        setRedoDrawStrokes((redoPrev) => [...redoPrev, removed]);
        drawingStrokesRef.current = next;
        drawingAppendQueueRef.current = [];
        drawingNeedsFullSyncRef.current = true;
        drawingDirtyRef.current = true;
      }
      return next;
    });
  };

  const handleRedoDrawing = () => {
    setRedoDrawStrokes((prevRedo) => {
      if (!prevRedo.length) {
        return prevRedo;
      }
      const nextRedo = [...prevRedo];
      const restored = nextRedo.pop();
      if (restored) {
        setLocalDrawStrokes((prev) => {
          const next = [...prev, restored];
          drawingStrokesRef.current = next;
          return next;
        });
        drawingAppendQueueRef.current = [];
        drawingNeedsFullSyncRef.current = true;
        drawingDirtyRef.current = true;
      }
      return nextRedo;
    });
  };

  const handleClearDrawing = () => {
    setLocalDrawStrokes([]);
    drawingStrokesRef.current = [];
    setRedoDrawStrokes([]);
    drawingAppendQueueRef.current = [];
    drawingNeedsFullSyncRef.current = true;
    drawingDirtyRef.current = true;
  };

  const handleZirkelDrawArc = useCallback(
    (cx: number, cy: number, radius: number, startDeg: number, endDeg: number) => {
      const points = arcPoints(cx, cy, radius, startDeg, endDeg);
      if (points.length < 2) return;
      const stroke = {
        id: createStrokeId(),
        tool: 'line' as const,
        color: activeDrawColor,
        width: drawWidth,
        alpha: 1,
        points,
        createdAt: new Date().toISOString(),
      };
      setLocalDrawStrokes((prev) => {
        const next = [...prev, stroke];
        drawingStrokesRef.current = next;
        return next;
      });
      setRedoDrawStrokes([]);
      drawingAppendQueueRef.current.push(stroke);
      drawingDirtyRef.current = true;
    },
    [activeDrawColor, drawWidth]
  );

  // ── Page navigation handlers ──────────────────────────────────────────────
  const handleSetPage = async (newIndex: number) => {
    const token = deviceTokenRef.current;
    if (!token || !status?.pin || isPageSwitching || newIndex < 0 || newIndex >= totalPages) return;
    setIsPageSwitching(true);
    try {
      await deviceSetActivePage({ deviceToken: token, pin: status.pin, pageIndex: newIndex });
    } catch { /* best-effort */ } finally {
      setIsPageSwitching(false);
    }
  };

  const handleAddPage = async () => {
    const token = deviceTokenRef.current;
    if (!token || !status?.pin || isPageSwitching) return;
    setIsPageSwitching(true);
    try {
      await deviceAddPage({ deviceToken: token, pin: status.pin });
    } catch { /* best-effort */ } finally {
      setIsPageSwitching(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-950 text-white">
      <div className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden">
        <div className="absolute left-4 top-4 z-[90] flex max-w-[65vw] items-center gap-2 overflow-hidden text-[10px] tracking-[0.2em] text-slate-500">
          <img src={logo} alt="Lernrakete Logo" className="h-3 w-auto opacity-80" />
          <span className="uppercase">Lernrakete</span>
          {status?.status === 'connected' && displayTheme ? (
            <span className="truncate normal-case tracking-normal text-slate-400">· {displayTheme}</span>
          ) : null}
          {status?.status !== 'connected' && status?.pin && (
            <span className="inline-flex items-center gap-1 rounded border border-slate-600 bg-slate-900/90 px-2 py-0.5 font-mono text-xs text-sky-300">
              {status.pin}
              {waitingPinInStatusBar && (
                <button
                  type="button"
                  onClick={() => setWaitingPinInStatusBar(false)}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                  title="Pin groß anzeigen"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleToggleFullscreen}
          className="absolute right-14 top-4 z-[90] inline-flex items-center justify-center rounded-full border border-slate-500 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-800"
          title={isFullscreen ? 'Vollbild verlassen' : 'Vollbild aktivieren'}
        >
          {isFullscreen ? 'Exit' : 'Vollbild'}
        </button>
        {status?.status === 'connected' && (
          <button
            type="button"
            onClick={handleReleaseConnection}
            disabled={isReleasing}
            className="absolute right-4 top-4 z-[90] inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            title="Verbindung freigeben"
          >
            {isReleasing ? '…' : 'X'}
          </button>
        )}
        {(status?.status === 'connected' || waitingPinInStatusBar) && !showLernbueroDashboard && !showLiveLessonBoard && (
          <div className="absolute bottom-4 left-4 right-4 z-[60] flex flex-nowrap items-center justify-start gap-2 overflow-x-auto rounded-xl border border-slate-600 bg-slate-900/95 px-3 py-2">
            {/* Seitennavigation */}
            <button
              type="button"
              onClick={() => handleSetPage(activePageIndex - 1)}
              disabled={activePageIndex <= 0 || isPageSwitching}
              className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-500 bg-slate-800 text-sm text-slate-200 disabled:opacity-30 hover:bg-slate-700"
              title="Vorherige Seite"
            >‹</button>
            <span className="min-w-[4.5rem] text-center text-xs text-slate-300">
              Seite {activePageIndex + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => handleSetPage(activePageIndex + 1)}
              disabled={activePageIndex >= totalPages - 1 || isPageSwitching}
              className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-500 bg-slate-800 text-sm text-slate-200 disabled:opacity-30 hover:bg-slate-700"
              title="Nächste Seite"
            >›</button>
            <button
              type="button"
              onClick={() => handleAddPage()}
              disabled={isPageSwitching}
              className="inline-flex h-8 items-center gap-1 rounded border border-slate-500 bg-slate-800 px-2 text-xs text-slate-200 disabled:opacity-30 hover:bg-slate-700"
              title="Neue Seite hinzufügen"
            >+ Seite</button>
            <button
              type="button"
              onClick={openDateienModal}
              className="inline-flex h-8 shrink-0 items-center gap-1 rounded border border-slate-500 bg-slate-800 px-2 text-slate-200 hover:bg-slate-700"
              title="Dateien – Bild/Video/PDF auf Display anzeigen"
            >
              <FolderOpen className="h-4 w-4" />
              <span className="text-xs">Dateien</span>
            </button>
            <button
              type="button"
              onClick={() => { setYoutubeError(null); setYoutubeUrl(''); setShowYouTubeModal(true); }}
              className="inline-flex h-8 shrink-0 items-center gap-1 rounded border border-slate-500 bg-slate-800 px-2 text-slate-200 hover:bg-slate-700"
              title="YouTube-Video auf Display anzeigen"
            >
              <Youtube className="h-4 w-4" />
              <span className="text-xs">YouTube</span>
            </button>
            <button
              type="button"
              onClick={() => {
                textBoxStartRef.current = null;
                setTextBoxDraft(null);
                setTextBoxDrawMode((v) => !v);
                setActiveDrawTool(null);
              }}
              className={`inline-flex h-8 shrink-0 items-center gap-1 rounded border px-2 ${
                textBoxDrawMode ? 'border-sky-400 bg-sky-500/20 text-sky-200' : 'border-slate-500 bg-slate-800 text-slate-200 hover:bg-slate-700'
              }`}
              title={textBoxDrawMode ? 'Rechteck aufziehen (nochmal klicken: abbrechen)' : 'Textfeld aufziehen und direkt tippen'}
            >
              <FileText className="h-4 w-4 shrink-0" />
              <span className="text-xs">Text</span>
            </button>
            <div className="mx-1 h-6 w-px shrink-0 bg-slate-600" />
            {/* Zeichenwerkzeuge */}
            <button
              type="button"
              onClick={() => setActiveDrawTool(null)}
              className={`inline-flex h-8 w-8 items-center justify-center rounded border text-xs ${
                isHandMode ? 'border-sky-400 bg-sky-500/20 text-sky-200' : 'border-slate-500 bg-slate-800 text-slate-200'
              }`}
              title="Hand-Modus (Objekte bedienen)"
            >
              ✋
            </button>
            {DRAW_TOOLS.map((tool) => {
              const isActive = activeDrawTool === tool;
              const label = tool === 'pen' ? '✎' : tool === 'marker' ? '🖍' : tool === 'line' ? '／' : tool === 'line-dashed' ? '− −' : tool === 'line-ruler' ? '▬▬' : '⌫';
              const title = tool === 'pen' ? 'Stift' : tool === 'marker' ? 'Marker' : tool === 'line' ? 'Linie' : tool === 'line-dashed' ? 'Gestrichelte Linie' : tool === 'line-ruler' ? 'Linie mit Skaleneinteilung' : 'Radierer';
              return (
                <button
                  key={tool}
                  type="button"
                  onClick={() => setActiveDrawTool(tool)}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded border text-xs ${
                    isActive ? 'border-sky-400 bg-sky-500/20 text-sky-200' : 'border-slate-500 bg-slate-800 text-slate-200'
                  }`}
                  title={title}
                >
                  {label}
                </button>
              );
            })}
            {/* Grundformen: ein Button, Dropdown per Portal oberhalb der Toolbar */}
            <div className="relative inline-block">
              <button
                ref={shapeButtonRef}
                type="button"
                onClick={() => setShowShapeMenu((v) => !v)}
                className={`inline-flex h-8 w-8 items-center justify-center rounded border text-xs ${
                  activeDrawTool && (SHAPE_TOOLS as readonly string[]).includes(activeDrawTool) ? 'border-sky-400 bg-sky-500/20 text-sky-200' : 'border-slate-500 bg-slate-800 text-slate-200'
                }`}
                title="Grundformen"
              >
                <Shapes className="h-4 w-4" />
              </button>
              {showShapeMenu && shapeMenuPosition && typeof document !== 'undefined' && createPortal(
                <div
                  className="fixed z-[200] rounded-lg border border-slate-600 bg-slate-800 py-1 shadow-xl"
                  style={{ left: shapeMenuPosition.left, bottom: shapeMenuPosition.bottom }}
                >
                  {(['rectangle', 'square', 'circle', 'oval', 'triangle'] as const).map((shape) => (
                    <button
                      key={shape}
                      type="button"
                      onClick={() => { setActiveDrawTool(shape); setShowShapeMenu(false); }}
                      className="block w-full px-3 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-700"
                    >
                      {shape === 'rectangle' ? 'Rechteck' : shape === 'square' ? 'Quadrat' : shape === 'circle' ? 'Kreis' : shape === 'oval' ? 'Oval' : 'Dreieck'}
                    </button>
                  ))}
                </div>,
                document.body
              )}
            </div>
            <div className="mx-1 h-6 w-px bg-slate-600" />
            {DRAW_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setActiveDrawColor(color)}
                className={`h-7 w-7 rounded-full border-2 ${activeDrawColor === color ? 'border-white' : 'border-slate-500'}`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
            <div className="mx-1 h-6 w-px bg-slate-600" />
            <label className="flex items-center gap-2 text-xs text-slate-200">
              <span>Dicke</span>
              <input
                type="range"
                min={1}
                max={24}
                value={drawWidth}
                onChange={(event) => setDrawWidth(Number(event.target.value) || 4)}
                className="w-24 accent-sky-400"
              />
              <span className="w-6 text-right">{drawWidth}</span>
            </label>
            <button
              type="button"
              onClick={handleUndoDrawing}
              disabled={localDrawStrokes.length === 0}
              className="inline-flex h-8 items-center justify-center rounded border border-slate-500 bg-slate-800 px-2 text-xs text-slate-200 disabled:opacity-40"
              title="Undo"
            >
              Undo
            </button>
            <button
              type="button"
              onClick={handleRedoDrawing}
              disabled={redoDrawStrokes.length === 0}
              className="inline-flex h-8 items-center justify-center rounded border border-slate-500 bg-slate-800 px-2 text-xs text-slate-200 disabled:opacity-40"
              title="Redo"
            >
              Redo
            </button>
            <button
              type="button"
              onClick={handleClearDrawing}
              disabled={localDrawStrokes.length === 0}
              className="inline-flex h-8 items-center justify-center rounded border border-red-500/70 bg-red-700/30 px-2 text-xs text-red-100 disabled:opacity-40"
              title="Zeichnung löschen"
            >
              Löschen
            </button>
            <button
                type="button"
                onClick={() => {
                  const willShow = !geodreieck.visible;
                  setGeodreieck((g) => ({ ...g, visible: !g.visible }));
                  if (willShow) setActiveDrawTool(null);
                }}
                className={`inline-flex h-8 w-8 items-center justify-center rounded border text-slate-200 hover:bg-slate-700 ${geodreieck.visible ? 'border-sky-400 bg-sky-500/20' : 'border-slate-500 bg-slate-800'}`}
                title="Geodreieck"
              >
                <Triangle className="h-4 w-4" />
              </button>
            <button
                type="button"
                onClick={() => {
                  const willShow = !calculator.visible;
                  setCalculator((c) => ({ ...c, visible: !c.visible }));
                  if (willShow) setActiveDrawTool(null);
                }}
                className={`inline-flex h-8 w-8 items-center justify-center rounded border text-slate-200 hover:bg-slate-700 ${calculator.visible ? 'border-sky-400 bg-sky-500/20' : 'border-slate-500 bg-slate-800'}`}
                title="Taschenrechner"
              >
                <Calculator className="h-4 w-4" />
              </button>
            <button
                type="button"
                onClick={() => {
                  const willShow = !zirkel.visible;
                  setZirkel((z) => ({ ...z, visible: !z.visible }));
                  if (willShow) setActiveDrawTool(null);
                }}
                className={`inline-flex h-8 w-8 items-center justify-center rounded border text-slate-200 hover:bg-slate-700 ${zirkel.visible ? 'border-sky-400 bg-sky-500/20' : 'border-slate-500 bg-slate-800'}`}
                title="Zirkel"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="8" r="2" /><path d="M12 10 8 22M12 10l4 12" /></svg>
              </button>
            {zirkel.visible && (
              <label className="inline-flex items-center gap-1.5 text-slate-300" title="Schenkellänge des Zirkels">
                <span className="text-[10px] whitespace-nowrap">Schenkel</span>
                <input
                  type="range"
                  min={LEG_LENGTH_MIN}
                  max={LEG_LENGTH_MAX}
                  value={Math.max(LEG_LENGTH_MIN, Math.min(LEG_LENGTH_MAX, zirkel.legLength ?? DEFAULT_ZIRKEL.legLength))}
                  onChange={(e) => setZirkel((z) => ({ ...z, legLength: Number(e.target.value) }))}
                  className="w-16 h-6 accent-sky-400"
                />
              </label>
            )}
            <div className="relative inline-block">
              <button
                ref={backgroundButtonRef}
                type="button"
                onClick={() => setShowBackgroundMenu((v) => !v)}
                className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-500 bg-slate-800 text-slate-200 hover:bg-slate-700"
                title="Hintergrund & Linien"
              >
                <Palette className="h-4 w-4" />
              </button>
              {showBackgroundMenu && backgroundMenuPosition && typeof document !== 'undefined' && createPortal(
                <div
                  className="fixed z-[200] w-48 rounded-lg border border-slate-600 bg-slate-800 p-2 shadow-xl"
                  style={{ left: backgroundMenuPosition.left, bottom: backgroundMenuPosition.bottom }}
                >
                  <p className="mb-1.5 px-1 text-[11px] font-medium text-slate-400">Hintergrund</p>
                  <div className="flex flex-wrap gap-1">
                    {BACKGROUND_PRESETS.map(({ label, value }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => { setBackgroundColor(value); }}
                        className={`h-6 rounded border px-2 text-[11px] ${backgroundColor === value ? 'border-sky-400 ring-1 ring-sky-400' : 'border-slate-600'}`}
                        style={{ backgroundColor: value, color: value === '#ffffff' || value === '#f1f5f9' ? '#1e293b' : '#e2e8f0' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 mb-1 px-1 text-[11px] font-medium text-slate-400">Hilfslinien</p>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs text-slate-200 hover:bg-slate-700">
                    <input
                      type="checkbox"
                      checked={showWritingLines}
                      onChange={(e) => setShowWritingLines(e.target.checked)}
                      className="rounded accent-sky-400"
                    />
                    Schreiblinien
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs text-slate-200 hover:bg-slate-700">
                    <input
                      type="checkbox"
                      checked={showMathGrid}
                      onChange={(e) => setShowMathGrid(e.target.checked)}
                      className="rounded accent-sky-400"
                    />
                    Rechenkaro
                  </label>
                  <p className="mt-2 mb-1 px-1 text-[11px] font-medium text-slate-400">Geodreieck</p>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs text-slate-200 hover:bg-slate-700">
                    <input
                      type="checkbox"
                      checked={geodreieck.visible}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setGeodreieck((g) => ({ ...g, visible: checked }));
                        if (checked) setActiveDrawTool(null);
                      }}
                      className="rounded accent-sky-400"
                    />
                    Anzeigen
                  </label>
                  <p className="mt-2 mb-1 px-1 text-[11px] font-medium text-slate-400">Taschenrechner</p>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs text-slate-200 hover:bg-slate-700">
                    <input
                      type="checkbox"
                      checked={calculator.visible}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setCalculator((c) => ({ ...c, visible: checked }));
                        if (checked) setActiveDrawTool(null);
                      }}
                      className="rounded accent-sky-400"
                    />
                    Anzeigen
                  </label>
                  <p className="mt-2 mb-1 px-1 text-[11px] font-medium text-slate-400">Zirkel</p>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs text-slate-200 hover:bg-slate-700">
                    <input
                      type="checkbox"
                      checked={zirkel.visible}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setZirkel((z) => ({ ...z, visible: checked }));
                        if (checked) setActiveDrawTool(null);
                      }}
                      className="rounded accent-sky-400"
                    />
                    Anzeigen
                  </label>
                </div>,
                document.body
              )}
            </div>
          </div>
        )}
        {/* Modal: Dateien – User-Dateien für Display auswählen */}
        {!showLernbueroDashboard && showDateienModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => setShowDateienModal(false)}>
            <div className="max-h-[80vh] w-full max-w-lg rounded-xl border border-slate-600 bg-slate-800 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-600 px-4 py-3">
                <div className="flex items-center gap-2">
                  {((dateienSource === 'lernrakete' && dateienFolderStack.length > 0) ||
                    (dateienSource === 'nextcloud' && ncPathStack.length > 0) ||
                    (dateienSource === 'onedrive' && odPathStack.length > 0)) && (
                    <button
                      type="button"
                      onClick={navigateDateienBack}
                      className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                      title="Zurück"
                    >
                      ‹
                    </button>
                  )}
                  <h2 className="text-sm font-semibold text-slate-200">Dateien – auf Display anzeigen</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDateienModal(false)}
                  className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-200"
                  title="Schließen"
                >
                  ✕
                </button>
              </div>
              <div className="flex gap-2 border-b border-slate-600 px-4 py-2">
                {lrAvailable && (
                  <button
                    type="button"
                    onClick={() => setDateienSource('lernrakete')}
                    className={`rounded px-3 py-1 text-xs font-medium ${dateienSource === 'lernrakete' ? 'bg-sky-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}
                  >
                    Lernrakete
                  </button>
                )}
                {ncAvailable && (
                  <button
                    type="button"
                    onClick={() => setDateienSource('nextcloud')}
                    className={`rounded px-3 py-1 text-xs font-medium ${dateienSource === 'nextcloud' ? 'bg-sky-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}
                  >
                    Nextcloud
                  </button>
                )}
                {odAvailable && (
                  <button
                    type="button"
                    onClick={() => setDateienSource('onedrive')}
                    className={`rounded px-3 py-1 text-xs font-medium ${dateienSource === 'onedrive' ? 'bg-sky-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}
                  >
                    OneDrive
                  </button>
                )}
              </div>
              <p className="border-b border-slate-600 px-4 py-1.5 text-xs text-slate-400">
                {dateienSource === 'nextcloud'
                  ? 'Nextcloud: Bilder, Video und PDF auf dem Display.'
                  : dateienSource === 'onedrive'
                  ? 'OneDrive: Bilder, Video und PDF auf dem Display.'
                  : 'Nur Bilder, Video und PDF. Klicke auf eine Datei, um sie auf dem Display zu zeigen.'}
              </p>
              <div className="overflow-y-auto p-4" style={{ maxHeight: 'calc(80vh - 120px)' }}>
                {dateienError && (
                  <p className="mb-3 rounded bg-red-900/40 px-3 py-2 text-sm text-red-200">{dateienError}</p>
                )}
                {dateienLoading ? (
                  <p className="py-8 text-center text-sm text-slate-400">Lade …</p>
                ) : dateienSource === 'onedrive' ? (
                  <div className="space-y-1">
                    {odContents.folders.map((folder) => (
                      <button
                        key={folder.path}
                        type="button"
                        onClick={() => navigateOdFolder(folder)}
                        className="flex w-full items-center gap-2 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-700"
                      >
                        <FolderOpen className="h-5 w-5 shrink-0 text-amber-400" />
                        {folder.name}
                      </button>
                    ))}
                    {odContents.files
                      .filter((f) => isDisplaySupportedFile(f.mime_type))
                      .map((file) => (
                        <button
                          key={file.path}
                          type="button"
                          onClick={() => presentOdFile(file)}
                          className="flex w-full items-center gap-2 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-600"
                        >
                          <span className="shrink-0 text-base">
                            {mimeToDisplayMediaType(file.mime_type) === 'image' ? '🖼' : mimeToDisplayMediaType(file.mime_type) === 'video' ? '🎬' : '📄'}
                          </span>
                          <span className="truncate">{file.original_name}</span>
                        </button>
                      ))}
                    {!dateienError && odContents.folders.length === 0 && odContents.files.filter((f) => isDisplaySupportedFile(f.mime_type)).length === 0 && (
                      <p className="py-4 text-center text-sm text-slate-500">Keine Ordner oder unterstützten Dateien in diesem OneDrive-Ordner.</p>
                    )}
                  </div>
                ) : dateienSource === 'nextcloud' ? (
                  <div className="space-y-1">
                    {ncContents.folders.map((folder) => (
                      <button
                        key={folder.path}
                        type="button"
                        onClick={() => navigateNcFolder(folder)}
                        className="flex w-full items-center gap-2 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-700"
                      >
                        <FolderOpen className="h-5 w-5 shrink-0 text-amber-400" />
                        {folder.name}
                      </button>
                    ))}
                    {ncContents.files
                      .filter((f) => isDisplaySupportedFile(f.mime_type))
                      .map((file) => (
                        <button
                          key={file.path}
                          type="button"
                          onClick={() => presentNcFile(file)}
                          className="flex w-full items-center gap-2 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-600"
                        >
                          <span className="shrink-0 text-base">
                            {mimeToDisplayMediaType(file.mime_type) === 'image' ? '🖼' : mimeToDisplayMediaType(file.mime_type) === 'video' ? '🎬' : '📄'}
                          </span>
                          <span className="truncate">{file.original_name}</span>
                        </button>
                      ))}
                    {!dateienError && ncContents.folders.length === 0 && ncContents.files.filter((f) => isDisplaySupportedFile(f.mime_type)).length === 0 && (
                      <p className="py-4 text-center text-sm text-slate-500">Keine Ordner oder unterstützten Dateien in diesem Nextcloud-Ordner.</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {dateienContents.folders.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => navigateDateienFolder(folder)}
                        className="flex w-full items-center gap-2 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-700"
                      >
                        <FolderOpen className="h-5 w-5 shrink-0 text-amber-400" />
                        {folder.name}
                      </button>
                    ))}
                    {dateienContents.files
                      .filter((f) => isDisplaySupportedFile(f.mime_type))
                      .map((file) => (
                        <button
                          key={file.file_id}
                          type="button"
                          onClick={() => presentDateienFile(file)}
                          className="flex w-full items-center gap-2 rounded-lg border border-slate-600 bg-slate-700/50 px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-600"
                        >
                          <span className="shrink-0 text-base">
                            {mimeToDisplayMediaType(file.mime_type) === 'image' ? '🖼' : mimeToDisplayMediaType(file.mime_type) === 'video' ? '🎬' : '📄'}
                          </span>
                          <span className="truncate">{file.original_name}</span>
                        </button>
                      ))}
                    {!dateienError && dateienContents.folders.length === 0 && dateienContents.files.filter((f) => isDisplaySupportedFile(f.mime_type)).length === 0 && (
                      <p className="py-4 text-center text-sm text-slate-500">Keine Ordner oder unterstützten Dateien (Bild, Video, PDF) in diesem Ordner.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {/* Modal: YouTube-Video */}
        {!showLernbueroDashboard && showYouTubeModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => setShowYouTubeModal(false)}>
            <div className="w-full max-w-md rounded-xl border border-slate-600 bg-slate-800 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-600 px-4 py-3">
                <h2 className="text-sm font-semibold text-slate-200">YouTube-Video auf Display</h2>
                <button type="button" onClick={() => setShowYouTubeModal(false)} className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-200">✕</button>
              </div>
              <div className="p-4 space-y-3">
                {youtubeError && <p className="rounded bg-red-900/40 px-3 py-2 text-sm text-red-200">{youtubeError}</p>}
                <input
                  type="url"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && presentYouTube()}
                  placeholder="https://www.youtube.com/watch?v=… oder youtu.be/…"
                  className="w-full rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-sky-400 focus:outline-none"
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setShowYouTubeModal(false)} className="rounded-lg border border-slate-600 bg-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-600">Abbrechen</button>
                  <button type="button" onClick={presentYouTube} disabled={!youtubeUrl.trim()} className="rounded-lg border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm text-white hover:bg-sky-500 disabled:opacity-40">Auf Display anzeigen</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* 1920×1080 logical canvas – scaled to fit the viewport; also shown when waiting + pin in status bar */}
        {!showLernbueroDashboard && !showLiveLessonBoard && (
        <div
          ref={canvasContainerRef}
          style={{
            position: 'absolute',
            left: `${(window.innerWidth - CANVAS_W * canvasScale) / 2}px`,
            top: `${(window.innerHeight - CANVAS_H * canvasScale) / 2}px`,
            width: `${CANVAS_W}px`,
            height: `${CANVAS_H}px`,
            transform: `scale(${canvasScale})`,
            transformOrigin: 'top left',
          }}
        >
          {(status?.status === 'connected' || waitingPinInStatusBar) && (
            <>
              {/* Committed canvas – all finished strokes, never cleared during drawing */}
              <canvas
                ref={drawingCanvasRef}
                className="absolute inset-0 z-[54] pointer-events-none"
                style={{ width: `${CANVAS_W}px`, height: `${CANVAS_H}px` }}
              />
              {/* Live canvas – current stroke only, pointer events land here */}
              <canvas
                ref={liveCanvasRef}
                className={`absolute inset-0 z-[55] ${activeDrawTool ? 'pointer-events-auto' : 'pointer-events-none'}`}
                style={{ width: `${CANVAS_W}px`, height: `${CANVAS_H}px`, touchAction: activeDrawTool ? 'none' : 'auto' }}
                onPointerDown={beginDrawing}
                onPointerMove={moveDrawing}
                onPointerUp={endDrawing}
                onPointerCancel={endDrawing}
              />
              {textBoxDrawMode && (
                <div
                  className="absolute inset-0 z-[56] cursor-crosshair bg-slate-900/35"
                  onPointerDown={(e) => {
                    if (e.button !== 0) return;
                    const p = pointerToCanvas(e);
                    textBoxStartRef.current = { x: p.x, y: p.y, pointerId: e.pointerId };
                    setTextBoxDraft({ x: p.x, y: p.y, width: 0, height: 0 });
                    e.currentTarget.setPointerCapture(e.pointerId);
                  }}
                  onPointerMove={(e) => {
                    const start = textBoxStartRef.current;
                    if (!start || start.pointerId !== e.pointerId) return;
                    const p = pointerToCanvas(e);
                    setTextBoxDraft({
                      x: Math.min(start.x, p.x),
                      y: Math.min(start.y, p.y),
                      width: Math.abs(p.x - start.x),
                      height: Math.abs(p.y - start.y),
                    });
                  }}
                  onPointerUp={(e) => {
                    const start = textBoxStartRef.current;
                    if (!start || start.pointerId !== e.pointerId) return;
                    const p = pointerToCanvas(e);
                    const draft = {
                      x: Math.min(start.x, p.x),
                      y: Math.min(start.y, p.y),
                      width: Math.abs(p.x - start.x),
                      height: Math.abs(p.y - start.y),
                    };
                    textBoxStartRef.current = null;
                    setTextBoxDraft(null);
                    setTextBoxDrawMode(false);
                    if (draft.width >= 40 && draft.height >= 30) void createTextItemFromDraft(draft);
                    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
                  }}
                  onPointerCancel={(e) => {
                    textBoxStartRef.current = null;
                    setTextBoxDraft(null);
                    setTextBoxDrawMode(false);
                    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
                  }}
                >
                  {textBoxDraft && (
                    <div
                      className="absolute border-2 border-sky-400 bg-sky-500/10"
                      style={{
                        left: `${textBoxDraft.x}px`,
                        top: `${textBoxDraft.y}px`,
                        width: `${textBoxDraft.width}px`,
                        height: `${textBoxDraft.height}px`,
                      }}
                    />
                  )}
                  <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded bg-slate-800/90 px-3 py-1 text-xs text-slate-200">
                    Rechteck aufziehen, dann direkt tippen
                  </div>
                </div>
              )}
              {/* Zirkel: Bogen-Vorschau während des Drehens am Griff (über Live-Canvas, unter dem Zirkel-Overlay) */}
              {zirkel.visible && (
                <canvas
                  ref={zirkelPreviewCanvasRef}
                  className="absolute inset-0 z-[55] pointer-events-none"
                  style={{ width: `${CANVAS_W}px`, height: `${CANVAS_H}px`, isolation: 'isolate' }}
                />
              )}
              {geodreieck.visible && (
                <div className="absolute inset-0 z-[180] pointer-events-none">
                  <GeodreieckOverlay
                    x={geodreieck.x}
                    y={geodreieck.y}
                    rotation={geodreieck.rotation}
                    scale={geodreieck.scale}
                    displayScale={canvasScale}
                    interactive={!activeDrawTool}
                    onMove={(x, y) => {
                      if (showMathGrid) {
                        const s = snapToGrid(x, y);
                        x = s.x;
                        y = s.y;
                      }
                      setGeodreieck((g) => ({ ...g, x, y }));
                    }}
                    onRotate={(rotation) => setGeodreieck((g) => ({ ...g, rotation }))}
                    onScale={(scale) => setGeodreieck((g) => ({ ...g, scale }))}
                    snapToGrid={showMathGrid ? snapToGrid : undefined}
                  />
                </div>
              )}
              {calculator.visible && (
                <div className="absolute inset-0 z-[180] pointer-events-none">
                  <CalculatorOverlay
                    x={calculator.x}
                    y={calculator.y}
                    displayScale={canvasScale}
                    interactive={!activeDrawTool}
                    onMove={(x, y) => setCalculator((c) => ({ ...c, x, y }))}
                  />
                </div>
              )}
              {zirkel.visible && (
                <div className="absolute inset-0 z-[180] pointer-events-none">
                  <ZirkelOverlay
                    x={zirkel.x}
                    y={zirkel.y}
                    radius={zirkel.radius}
                    legLength={zirkel.legLength ?? DEFAULT_ZIRKEL.legLength}
                    displayScale={canvasScale}
                    interactive={!activeDrawTool}
                    onMove={(x, y) => {
                      if (showMathGrid) {
                        const s = snapToGrid(x, y);
                        x = s.x;
                        y = s.y;
                      }
                      setZirkel((z) => ({ ...z, x, y }));
                    }}
                    onRadiusChange={(radius) => setZirkel((z) => ({ ...z, radius }))}
                    onDrawArc={handleZirkelDrawArc}
                    onArcPreview={setZirkelArcPreview}
                    snapToGrid={showMathGrid ? snapToGrid : undefined}
                  />
                </div>
              )}
              {visibleMediaItems.length > 0 && (
                <div className="absolute inset-0 z-[57] pointer-events-none">
                    {[...visibleMediaItems].sort((a, b) => (mediaLayout[a.id]?.z || 0) - (mediaLayout[b.id]?.z || 0)).map((item, index) => {
                      const layout = mediaLayout[item.id] || { x: 0, y: 0, widthPx: 520, z: index + 1, visible: false };
                      const textBodyFontPx = Math.max(18, Math.min(72, Math.round(layout.widthPx * 0.06)));
                      const textTitleFontPx = Math.max(11, Math.min(26, Math.round(layout.widthPx * 0.024)));
                      const handlesVisible = activeHandlesItemId === item.id;
                      const handleVisibilityClass = handlesVisible
                        ? handlesFading
                          ? 'opacity-0'
                          : 'opacity-100'
                        : 'opacity-0 pointer-events-none';
                      return (
                        <div
                          key={item.id}
                          data-media-item-shell="true"
                          className="absolute left-1/2 top-1/2 pointer-events-auto"
                          style={{
                            zIndex: 30 + (layout.z || index),
                            transform: `translate(calc(-50% + ${layout.x}px), calc(-50% + ${layout.y}px)) rotate(${layout.rotationDeg || 0}deg)`
                          }}
                        >
                          <div
                            className="relative inline-block rounded-2xl border border-emerald-600/40 bg-slate-900 p-2 shadow-2xl"
                            style={{ touchAction: 'none' }}
                            onPointerDown={() => activateHandles(item.id)}
                          >
                            <button
                              type="button"
                              onPointerDown={(event) => {
                                activateHandles(item.id);
                                handleStartMediaDrag(item.id, 'move', event);
                              }}
                              className={`absolute left-2 right-2 -top-5 z-30 inline-flex h-6 items-center justify-center rounded border border-slate-500 bg-slate-900/95 text-[10px] text-slate-200 hover:bg-slate-800 transition-opacity duration-500 ${handleVisibilityClass}`}
                              title="Verschieben"
                            >
                              ↔ Verschieben
                            </button>
                            <div
                              className={`absolute -right-3 -top-3 z-30 inline-flex items-center gap-1 transition-opacity duration-500 ${handleVisibilityClass}`}
                              data-no-drag="true"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  activateHandles(item.id);
                                  moveLayer(item.id, 'up');
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800"
                                title="Ebene rauf"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  activateHandles(item.id);
                                  moveLayer(item.id, 'down');
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800"
                                title="Ebene runter"
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  activateHandles(item.id);
                                  setMediaLayout((prev) => ({
                                    ...prev,
                                    [item.id]: {
                                      ...(prev[item.id] || { x: 0, y: 0, visible: true, widthPx: 520, z: 1, rotationDeg: 0 }),
                                      widthPx: 260
                                    }
                                  }));
                                  scheduleLayoutSync();
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-sm text-slate-100 hover:bg-slate-800"
                                title="Minimieren"
                              >
                                –
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  activateHandles(item.id);
                                  setMediaLayout((prev) => ({
                                    ...prev,
                                    [item.id]: {
                                      ...(prev[item.id] || { x: 0, y: 0, visible: true, widthPx: 520, z: 1, rotationDeg: 0 }),
                                      widthPx: Math.max(420, CANVAS_W * 0.9)
                                    }
                                  }));
                                  scheduleLayoutSync();
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800"
                                title="Maximieren"
                              >
                                ⤢
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  activateHandles(item.id);
                                  handleDismissMediaItem(item.id);
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800"
                                title="Schließen"
                              >
                                X
                              </button>
                            </div>
                            <button
                              type="button"
                              data-no-drag="true"
                              onPointerDown={(event) => {
                                activateHandles(item.id);
                                handleStartMediaDrag(item.id, 'resize', event);
                              }}
                              style={{ touchAction: 'none' }}
                              className={`absolute -bottom-3 -right-3 z-30 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-500 bg-slate-900 text-xs text-slate-100 hover:bg-slate-800 transition-opacity duration-500 ${handleVisibilityClass}`}
                              title="Größe + Drehen"
                            >
                              ◢
                            </button>
                            <div className="flex items-center justify-center" data-no-drag data-media-item-content="true">
                              {item.type === 'text' ? (
                                <div
                                  style={{ width: `${layout.widthPx}px`, maxWidth: `${CANVAS_W * 0.96}px` }}
                                  className={`transition-opacity duration-700 outline-none ${layout.visible ? 'opacity-100' : 'opacity-0'}`}
                                  ref={(el) => {
                                    if (el && !layout.visible) {
                                      setMediaLayout((prev) => ({
                                        ...prev,
                                        [item.id]: { ...(prev[item.id] || { x: 0, y: 0, widthPx: layout.widthPx, z: layout.z || 1, rotationDeg: layout.rotationDeg || 0 }), visible: true }
                                      }));
                                    }
                                  }}
                                >
                                  <div
                                    ref={(el) => {
                                      if (el) {
                                        if (!textItemRefs.current[item.id]) textItemRefs.current[item.id] = { titleEl: null, bodyEl: null };
                                        textItemRefs.current[item.id].titleEl = el;
                                      }
                                    }}
                                    contentEditable
                                    suppressContentEditableWarning
                                    className="mb-3 font-semibold uppercase tracking-widest text-emerald-400 outline-none empty:before:content-['Titel'] empty:before:text-slate-500"
                                    style={{ fontSize: `${textTitleFontPx}px`, minHeight: `${textTitleFontPx}px` }}
                                    onFocus={() => setEditingTextItemId(item.id)}
                                    onInput={(e) => {
                                      const title = e.currentTarget.innerText.replace(/\s+/g, ' ').trim();
                                      scheduleTextItemUpdate(item.id, { title: title || undefined });
                                    }}
                                    onBlur={(e) => {
                                      const titleEl = e.currentTarget;
                                      const title = titleEl.innerText.replace(/\s+/g, ' ').trim();
                                      setEditingTextItemId(null);
                                      flushTextItemUpdate(item.id, { title: title || undefined });
                                    }}
                                  />
                                  <div
                                    ref={(el) => {
                                      if (el) {
                                        if (!textItemRefs.current[item.id]) textItemRefs.current[item.id] = { titleEl: null, bodyEl: null };
                                        textItemRefs.current[item.id].bodyEl = el;
                                      }
                                    }}
                                    contentEditable
                                    suppressContentEditableWarning
                                    className="whitespace-pre-wrap font-medium leading-relaxed text-white outline-none empty:before:content-['Text_eingeben…'] empty:before:text-slate-500"
                                    style={{ fontSize: `${textBodyFontPx}px` }}
                                    onFocus={() => setEditingTextItemId(item.id)}
                                    onInput={(e) => {
                                      scheduleTextItemUpdate(item.id, { text: e.currentTarget.innerText });
                                    }}
                                    onBlur={(e) => {
                                      const bodyEl = e.currentTarget;
                                      const text = bodyEl.innerText;
                                      setEditingTextItemId(null);
                                      flushTextItemUpdate(item.id, { text });
                                    }}
                                  />
                                </div>
                              ) : item.type === 'media' ? (
                                item.mediaType === 'video' ? (
                                  <video
                                    src={item.mediaUrl}
                                    controls
                                    autoPlay
                                    onLoadedData={() =>
                                      setMediaLayout((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...(prev[item.id] || { x: 0, y: 0, widthPx: 520, z: layout.z || 1 }),
                                          visible: true
                                        }
                                      }))
                                    }
                                    style={{ width: `${layout.widthPx}px`, maxWidth: `${CANVAS_W * 0.96}px` }}
                                    className={`h-auto max-h-[972px] rounded-xl object-contain transition-opacity duration-700 ${
                                      layout.visible ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  />
                                ) : item.mediaType === 'pdf' ? (
                                  <iframe
                                    src={buildPdfDisplayUrl(item.mediaUrl)}
                                    title={item.title || 'Display PDF'}
                                    onLoad={() =>
                                      setMediaLayout((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...(prev[item.id] || { x: 0, y: 0, widthPx: 820, z: layout.z || 1 }),
                                          visible: true
                                        }
                                      }))
                                    }
                                    style={{
                                      width: `${Math.max(layout.widthPx, 220)}px`,
                                      height: `${Math.round(Math.max(layout.widthPx, 220) * 1.35)}px`,
                                      pointerEvents: isDragging ? 'none' : 'auto'
                                    }}
                                    className={`rounded-xl border border-slate-700 bg-white transition-opacity duration-700 ${
                                      layout.visible ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  />
                                ) : item.mediaType === 'youtube' ? (
                                  <iframe
                                    src={buildYouTubeEmbedUrl(item.mediaUrl)}
                                    title={item.title || 'Display YouTube'}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    onLoad={() =>
                                      setMediaLayout((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...(prev[item.id] || { x: 0, y: 0, widthPx: 820, z: layout.z || 1 }),
                                          visible: true
                                        }
                                      }))
                                    }
                                    style={{
                                      width: `${Math.max(layout.widthPx, 320)}px`,
                                      height: `${Math.round(Math.max(layout.widthPx, 320) * 0.5625)}px`,
                                      pointerEvents: isDragging ? 'none' : 'auto'
                                    }}
                                    className={`rounded-xl border border-slate-700 bg-black transition-opacity duration-700 ${
                                      layout.visible ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  />
                                ) : (
                                  <img
                                    src={item.mediaUrl}
                                    alt={item.title || 'Display Medium'}
                                    onLoad={() =>
                                      setMediaLayout((prev) => ({
                                        ...prev,
                                        [item.id]: {
                                          ...(prev[item.id] || { x: 0, y: 0, widthPx: 520, z: layout.z || 1 }),
                                          visible: true
                                        }
                                      }))
                                    }
                                    style={{ width: `${layout.widthPx}px`, maxWidth: `${CANVAS_W * 0.96}px` }}
                                    className={`h-auto max-h-[972px] rounded-xl object-contain transition-opacity duration-700 ${
                                      layout.visible ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  />
                                )
                              ) : (
                                <img
                                  src={(item as any).imageUrl}
                                  alt={item.title || 'Display Bild'}
                                  onLoad={() =>
                                    setMediaLayout((prev) => ({
                                      ...prev,
                                      [item.id]: {
                                        ...(prev[item.id] || { x: 0, y: 0, widthPx: 520, z: layout.z || 1 }),
                                        visible: true
                                      }
                                    }))
                                  }
                                  style={{ width: `${layout.widthPx}px`, maxWidth: `${CANVAS_W * 0.96}px` }}
                                  className={`h-auto max-h-[972px] rounded-xl object-contain transition-opacity duration-700 ${
                                    layout.visible ? 'opacity-100' : 'opacity-0'
                                  }`}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </>
          )}
        </div>
        )}

        {/* Theme placeholder – outside scaled canvas, centred in viewport */}
        {!showLernbueroDashboard && !showLiveLessonBoard && !isLoading && !error && status?.status === 'connected' && visibleMediaItems.length === 0 && localDrawStrokes.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="mt-6 rounded-2xl border border-emerald-600/40 bg-slate-900 px-8 py-10 shadow-2xl">
              <div className="whitespace-pre-wrap text-4xl font-bold leading-tight text-white md:text-6xl xl:text-7xl">
                {displayTheme || 'Unterricht'}
              </div>
            </div>
          </div>
        )}
        {!showLernbueroDashboard && !showLiveLessonBoard && competencyToasts.length > 0 && (
          <div className="pointer-events-none absolute right-6 top-6 z-[230] flex max-w-[40vw] flex-col gap-2">
            {competencyToasts.map((entry) => {
              const isPositive = entry.delta > 0;
              if (entry.mode === 'ranking') {
                return <RankingToastCard key={entry.id} entry={entry} />;
              }
              return (
                <div
                  key={entry.id}
                  className={`rounded-xl border px-3 py-2 shadow-2xl backdrop-blur-sm ${
                    isPositive
                      ? 'border-emerald-400/60 bg-emerald-600/25 text-emerald-50'
                      : 'border-rose-400/60 bg-rose-600/25 text-rose-50'
                  }`}
                >
                  <div className="flex items-center gap-2 text-sm md:text-base">
                    {isPositive ? (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-400/30 text-emerald-100">
                        <Plus className="h-5 w-5 stroke-[3]" />
                      </span>
                    ) : (
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-rose-400/30 text-rose-100">
                        <Minus className="h-5 w-5 stroke-[3]" />
                      </span>
                    )}
                    <span className="font-semibold">{entry.fullName}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {showLernbueroDashboard && lernbueroDashboard && (
          <LernbueroDashboardView dashboard={lernbueroDashboard} nowMs={dashboardNowMs} />
        )}
        {showLiveLessonBoard ? <ClassroomBoardView boardDisplayState={liveLessonBoardState} /> : null}
        {isLiveLessonSource && !showLernbueroDashboard ? (
          <LiveLessonDisplayOverlayStack overlays={liveLessonOverlays} />
        ) : null}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-700 border-t-sky-400" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <div className="rounded-md border border-red-400/50 bg-red-500/20 px-4 py-3 text-red-100">{error}</div>
          </div>
        )}
        {!isLoading && !error && status?.status !== 'connected' && !waitingPinInStatusBar && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-6">
            {hasNoDisplayContent && (
              <>
                <img
                  src={idleLogo}
                  alt="Lernrakete Logo"
                  className="h-56 w-auto max-w-[74vw] drop-shadow-[0_0_26px_rgba(56,189,248,0.85)] drop-shadow-[0_0_72px_rgba(37,99,235,0.6)] md:h-72"
                />
                <p className="max-w-5xl px-6 text-center text-2xl font-semibold text-slate-100 md:text-4xl">
                  {waitingQuote}
                </p>
              </>
            )}
            <p className="text-xs uppercase tracking-[0.35em] text-slate-400">PIN zum Koppeln</p>
            <div className="mt-2 rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3 shadow-2xl">
              <div className="font-mono text-3xl font-bold tracking-[0.22em] text-sky-300 md:text-5xl">
                {status?.pin || '------'}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setWaitingPinInStatusBar(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-800 px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-700"
              title="Pin in die Statusleiste verschieben und Whiteboard freigeben"
            >
              <ChevronUp className="h-4 w-4" />
              Pin verkleinern
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
